import { useMemo, useState } from 'react';
import PhotoLightboxWithInfo from '../components/PhotoLightboxWithInfo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../hooks/useLanguage';

const FEATURES = [
 {key:"skis",title:"Лыжи",titleEn:"Skids"},
 {key:"legs",title:"Ноги",titleEn:"Legs"},
 {key:"table",title:"Стол",titleEn:"Table"},
 {key:"trunk",title:"Хобот и лапки",titleEn:"Trail and lugs"},
 {key:"trunkLock",title:"Блокировка хобота",titleEn:"Trail locking mechanism"},
 {key:"axle",title:"Боевая ось",titleEn:"Main axle"},
 {key:"swivel",title:"Вертлюг",titleEn:"Swivel"},
 {key:"clamp",title:"Хомут",titleEn:"Clamp"},
 {key:"rearRuler",title:"Задняя радиусная линейка",titleEn:"Rear traverse scale"},
 {key:"coarse",title:"Грубая наводка",titleEn:"Coarse traverse mechanism"},
 {key:"fine",title:"Тонкая наводка",titleEn:"Fine traverse mechanism"},
 {key:"level",title:"Уровень",titleEn:"Level"},
 {key:"link",title:"Связь («майка»)",titleEn:"Connecting yoke"},
 {key:"seat",title:"Сидушка / катушка",titleEn:"Seat / reel"},
 {key:"shieldSpool",title:"Катушка щита",titleEn:"Shield reel"}
];

const copy = {
  RU: {
    title: 'Определить внешний вид царского станка Соколова',
    intro: 'Укажите известные данные о станке. Определитель покажет наиболее вероятную модификацию каждого узла, известные серийные границы и опорные экземпляры.',
    question: 'Какой станок интересует?',
    searchHelp: 'Поиск выполняется по серийному номеру или году.',
    manufacturer: 'Производитель',
    toz: 'ТОЗ — Тула',
    orz: 'ОРЗ',
    unknownMaker: 'Неизвестен / сравнить',
    search: 'Искать',
    bySerial: 'По номеру',
    byYear: 'По году',
    year: 'Год',
    selectYear: 'Выберите год',
    serial: 'Серийный номер',
    serialPlaceholder: 'например, 8037',
    show: 'Показать',
    unknownManufacturer: 'Производитель неизвестен',
    yearSuffix: 'год',
    serialResult: 'Поиск выполнен по серийному номеру. Если точная граница неизвестна, ближайшая известная деталь используется как основной ориентир.',
    yearResult: 'Поиск выполнен по году. В списке известных деталей отмечено положение выбранного года; детали этого года подсвечены.',
    knownParts: 'Известные детали',
    here: 'МЫ ТУТ',
    knownPart: 'известная деталь',
    partFromYear: (year) => `Деталь ${year} года`,
    observation: 'Наблюдение / реконструкция',
    sourceDetail: 'отдельная деталь',
    sourceExample: 'экземпляр',
    sourceSearch: 'поиск по базе',
    openPhoto: 'Открыть фото',
    openGallery: 'Открыть галерею детали',
    partPhotos: 'Фото детали',
  },
  EN: {
    title: 'Identify an Imperial-era Sokolov mount',
    intro: 'Enter what is known about the mount. The identifier will show the most likely form of each component, documented serial boundaries and reference examples.',
    question: 'Which mount are you researching?',
    searchHelp: 'Search by serial number or production year.',
    manufacturer: 'Manufacturer',
    toz: 'TOZ — Tula',
    orz: 'ORZ',
    unknownMaker: 'Unknown / compare both',
    search: 'Search by',
    bySerial: 'Serial number',
    byYear: 'Year',
    year: 'Year',
    selectYear: 'Select a year',
    serial: 'Serial number',
    serialPlaceholder: 'for example, 8037',
    show: 'Show result',
    unknownManufacturer: 'Unknown manufacturer',
    yearSuffix: '',
    serialResult: 'The search uses the serial number. When an exact boundary is unknown, the nearest documented component is used as the main reference.',
    yearResult: 'The search uses the production year. The selected year is marked among the documented components, and direct matches are highlighted.',
    knownParts: 'Documented components',
    here: 'SELECTED',
    knownPart: 'documented component',
    partFromYear: (year) => `${year} component`,
    observation: 'Observation / reconstruction',
    sourceDetail: 'individual component',
    sourceExample: 'complete example',
    sourceSearch: 'catalogue search',
    openPhoto: 'Open photo',
    openGallery: 'Open component gallery',
    partPhotos: 'Component photos',
  },
};

function featureTitle(feature, lang) {
  return lang === 'EN' ? feature.titleEn : feature.title;
}

/*
  BASE = established period configuration.
  It is deliberately broader than exact numbered points: if a feature is known to remain
  unchanged across a period, a 1912 query should not become "unknown" just because we do not
  have a separate 1912 database card for every nut and bracket.
*/
const BASE = {
 TOZ:{
  early1909:{
   years:[1909],
   _confidence:"medium",
   _reason:"Это признаки станка образца 1909 года, подтверждённые ранними сохранившимися экземплярами и отдельными деталями. Они не означают, что любой комплект с датой 1909 сохранился без позднейших замен.",
   skis:["Стальные ранние лыжи.","Пропил под ноги присутствует."],
   legs:["Стальные ноги присутствуют."],
   table:["Стальной стол раннего образца.","Радиусная шкала выполнена как часть стола.","У вертлюга имеются боковые ограничители."],
   trunk:["Стальной хобот раннего исполнения."],
   trunkLock:["Ранний сложный подпружиненный механизм."],
   axle:["Ранняя фигурная боевая ось с проточками."],
   swivel:["Стальной ранний вертлюг с боковыми ограничителями."],
   clamp:["Стальной ранний хомут.","«Рогов» под отдельную заднюю радиусную линейку ещё нет."],
   rearRuler:["Отдельной задней радиусной линейки ещё нет: шкала связана со столом."],
   coarse:["Ранний стальной механизм грубой наводки: 5 позиций, с прорезью."],
   link:["Стальная связь («майка»).","Номер наносится на одной стороне, год — на другой."],
   seat:["Сидушка/катушка относится к ранней штатной схеме; на сохранившихся финских комплектах этот узел часто утрачен или заменён."]
  },
  ambiguous1910:{
   years:[1910],
   _confidence:"low",
   _reason:"1910 год пока нельзя привязать к одной конфигурации: ранний вариант образца 1909 ещё существовал, но следующий вариант станка уже мог появиться. Точная граница между ними не установлена.",
   skis:["Лыжи стальные; ноги и пропил под них ещё относятся к ранней общей схеме."],
   legs:["Стальные ноги должны сохраняться в обоих известных ранних вариантах."],
   table:["Возможны два состояния: ранний стол образца 1909 с интегрированной шкалой и ограничителями либо уже следующий стальной стол с отдельной задней радиусной линейкой.","Без номера/фотографий точнее определить нельзя."],
   trunk:["Стальной хобот раннего периода."],
   trunkLock:["Ожидается сложный подпружиненный механизм ранней схемы, но конкретный вариант лучше подтверждать по самому экземпляру."],
   axle:["Ожидается ранняя фигурная боевая ось с проточками."],
   swivel:["Возможен ранний вертлюг с ограничителями или уже следующий вариант без них."],
   clamp:["Возможен ранний хомут без «рогов» либо уже хомут с «рогами» под отдельную заднюю линейку."],
   rearRuler:["Возможен как самый ранний вариант без отдельной задней линейки, так и уже вариант с отдельной линейкой."],
   coarse:["Для обоих ранних вариантов известен стальной механизм грубой наводки: 5 позиций, с прорезью."],
   link:["Связь стальная; конкретную форму и маркировку лучше подтверждать по номерной детали."],
   seat:["Сидушка/катушка ещё относится к ранней штатной схеме; сохранность этого узла на дошедших экземплярах низкая."]
  },
  steelPeriod:{
   years:[1911,1912,1913,1914],
   _confidence:"medium",
   _reason:"Это ожидаемая конфигурация периода, восстановленная по нескольким известным экземплярам и номерным деталям. Для конкретного станка её нужно проверять по номеру и фотографиям.",
   skis:["Стальные лыжи.","Пропил под ноги присутствует."],
   legs:["Стальные ноги присутствуют."],
   table:["Стальной стол.","Боковые вырезы под нажимы присутствуют.","У ТОЗ имеется характерная выраженная выборка/опора под отдельную заднюю радиусную линейку."],
   trunk:["Стальной хобот.","Ранние лапки имеют фигурные проточки."],
   trunkLock:["Сложный подпружиненный механизм ранней схемы.","Ручка раннего исполнения — полая."],
   axle:["Ранняя фигурная боевая ось с проточками."],
   swivel:["Стальной вертлюг.","Боковых ограничителей самого раннего образца уже нет.","Снизу появляется маслёнка/масляное отверстие."],
   clamp:["Стальной хомут с «рогами» для работы с задней радиусной линейкой.","Для ТОЗ характерна мелкая точечная насечка флажка."],
   rearRuler:["Отдельная задняя радиусная линейка присутствует.","У тульской линейки есть дополнительное отверстие посередине для крепления квадратика; это характерное отличие от ОРЗ."],
   coarse:["Стальной ранний механизм: 5 позиций, с прорезью."],
   link:["Стальная связь («майка»).","Номер — на одной стороне, год — на другой."],
   seat:["Штатная сидушка/катушка ещё применяется; отсутствие её на финском экземпляре само по себе не является датирующим признаком."]
  },
  transition1915:{
   years:[1915],
   _confidence:"low",
   _reason:"1915 год переходный: разные узлы менялись не одновременно. Без номера конкретного узла многие признаки нужно читать как диапазон между известными номерными точками.",
   skis:["Лыжи ещё стальные, но внутри 1915 года меняется качество исполнения.","ТОЗ №3681 и №3920 подтверждают тонкие стальные лыжи с пропилами под ноги; число отверстий по фото 15–16, точно не читается.","К №4541 лыжи уже утолщённые и грубо обработанные, при этом пропилы под ноги и сами ноги ещё сохраняются; число отверстий также читается как 15–16."],
   legs:["Ноги ещё штатно присутствуют; на ТОЗ №3681 и №4541 они сохранились.","На №3920 ноги утрачены, но пропилы под них на лыжах есть, поэтому этот экземпляр не используется как доказательство заводской схемы без ног."],
   table:["1915 год — переход от стальных столов к бронзовым.","ТОЗ №3681, №3920, №4330 и №4541 уже имеют бронзовые столы, при этом ранняя геометрия ещё полностью сохраняется: боковые вырезы под нажимы и выраженная тульская опора/посадка под заднюю радиусную линейку есть."],
   trunk:["На ТОЗ №3681 и №3920 лапки ещё стальные и фигурные, ранней схемы.","К №4419 и №4541 стальные лапки уже известны без боковых фигурных выборок; переход происходит внутри 1915 года."],
   trunkLock:["Сложный подпружиненный механизм ещё сохраняется; на ТОЗ №3681 и №4541 ручка ещё полая.","№3920 имеет необычную блокировку с элементом в виде «плавничка»; возможно ремонтное исполнение, поэтому как серийную границу её не используем."],
   axle:["Ранняя фигурная ось всё ещё сохраняется; её подтверждают ТОЗ №3681, №3920, №4084 и №4541."],
   swivel:["Стальной вертлюг с боковыми отверстиями сохраняется на ТОЗ №3681, №3920 и ещё на №4541; №3681 и №4541 рассчитаны на раннюю 5-позиционную грубую наводку.","Переход к бронзовому вертлюгу происходит позднее, а исчезновение боковых отверстий — ещё позже."],
   clamp:["Стальной хомут с «рогами» ещё применяется; №3219 и комплектный №4541 подтверждают эту схему в 1915 году."],
   rearRuler:["Задняя радиусная линейка ещё применяется.","У тульской линейки есть дополнительное отверстие посередине для крепления квадратика; у ОРЗ такого отверстия нет."],
   coarse:["Ранний стальной механизм на 5 позиций с прорезью остаётся нормальным вариантом; на ТОЗ №3681 и №4541 он подтверждён как родной.","На №3920 родная грубая наводка не сохранилась."],
   link:["Связь в начале периода стальная; ТОЗ №3681 и №3920 подтверждают стальные «майки».","На №4541 майка визуально выглядит стальной, но номер и год на ней не читаются, поэтому как прямую номерную границу её не используем. Переход на бронзу происходит позднее."],
   seat:["На №4541 сидушка ещё подтверждена как штатная; к №5691 уже применяется заводское жёстко закреплённое кольцо вместо неё.","Точная граница перехода пока находится между этими номерными точками."]
  },
  late1916:{
   years:[1916],
   _confidence:"low",
   _reason:"1916 год содержит много последовательных переходов. При наличии серийного номера результат нужно читать по ближайшим номерным точкам, а не как единую конфигурацию всего года.",
   skis:["В начале/середине периода встречаются грубо обработанные утолщённые стальные лыжи на 16 отверстий с пропилом под ноги (№5691, №6276).","Позднее появляются бронзовые лыжи на 6 отверстий; к №10679 пропил под ноги уже исчезает."],
   legs:["Ноги сохраняются значительную часть 1916 года. Для финских экземпляров главным признаком считаем пропилы под ноги в лыжах: сами ноги финны снимали почти со всех станков.","На №5691 и №6276 пропилы ещё есть; к №10679 пропилы уже исчезают."],
   table:["Стол бронзовый на поздних номерах.","К №8037 боковые вырезы под нажимы уже исчезли, но посадка задней линейки ещё есть; к №9326 исчезает и она."],
    trunk:["К №5691 уже подтверждены стальные лапки без фигурных проточек; №6207 показывает то же исполнение.","Поздние тульские лапки остаются стальными и упрощёнными."],
    trunkLock:["На №5691 сложный подпружиненный механизм ещё сохраняется, но ручка уже сплошная, не полая; №6207 и №6799 подтверждают то же переходное состояние.","К №8037 механизм уже упрощён."],
    axle:["На №5691 ещё стоит ранняя фигурная боевая ось с проточками.","К №9122 уже подтверждена поздняя округлая упрощённая ось; переход лежит между этими номерными точками."],
   swivel:["Материал уже меняется на бронзу, при этом форма сначала остаётся прежней: №6134/6612 ещё имеют боковые отверстия.","К №8037 отверстия исчезают; все известные более поздние царские ТОЗ сохраняют бронзовый вертлюг без них."],
   clamp:["В начале/середине 1916 ещё встречаются «рога» под заднюю линейку (№6134); к №10679 хомут уже без «рогов»."],
   rearRuler:["К №8037 посадка под заднюю линейку ещё есть; к №9326 уже исчезает.","Для самой тульской задней линейки характерно дополнительное центральное отверстие под крепление квадратика; у ОРЗ оно отсутствует."],
   coarse:["Все известные царские тульские механизмы грубой наводки стальные.","До №6612 сохраняется ранний 5-позиционный механизм; к №8037/8367 появляется поздний вариант на 3 отверстия."],
    link:["К №5691, 1916 бронзовая связь («майка») уже подтверждена напрямую; №6200 подтверждает продолжение этого исполнения.","Переход сталь → бронза лежит между №3060, 1915 и №5691, 1916."],
   seat:["К №5691 сидушка уже конструктивно не предусматривается: вместо неё стоит заводское жёстко закреплённое кольцо."]
  },
  late1917:{
   years:[1917],
   _confidence:"medium",
   _reason:"Для 1917 года поздние состояния большинства узлов уже сложились в 1916-м и далее не возвращаются к ранней схеме. Поэтому результат наследует последнюю подтверждённую конфигурацию и использует прямые точки 1917 года там, где они есть.",
   skis:["Наиболее вероятно сохраняются поздние бронзовые лыжи на 6 отверстий без пропила под ноги — такое состояние уже подтверждено на ТОЗ №10679 в 1916 году."],
   legs:["Ноги отсутствуют: к №10679, 1916 исчезают и сами ноги, и пропилы под них."],
   table:["Поздний бронзовый стол.","Боковых вырезов под нажимы нет.","Посадочного места под заднюю радиусную линейку нет; это прямо подтверждено на ТОЗ №16253, 1917."],
   trunk:["Поздний упрощённый хобот; на поздних тульских номерах известны стальные гладкие лапки без фигурных проточек."],
   trunkLock:["Упрощённый механизм блокировки хобота; он уже подтверждён на поздних станках 1916 года."],
   axle:["Округлая упрощённая боевая ось; поздняя схема подтверждена на №9122, №10101 и №10679 в 1916 году."],
   swivel:["Бронзовый вертлюг без боковых отверстий; такое исполнение подтверждено с №8037 и сохраняется на всех известных более поздних царских ТОЗ."],
   clamp:["Поздний хомут без «рогов»; №10679 уже без рогов, №14818, 1917 подтверждает сохранение этой схемы."],
   rearRuler:["Задняя радиусная линейка и её посадочное место отсутствуют; к №9326, 1916 посадка уже исчезла и на №16253, 1917 её также нет."],
   coarse:["Наиболее вероятен поздний стальной механизм на 3 отверстия: он уже появляется на №8037/8367, а вертлюг №10679 рассчитан на эту схему."],
   link:["Бронзовая связь («майка») типична для позднего периода; переход на бронзу подтверждён уже к №6200, 1916."],
   seat:["Поздняя схема без сидушки: после подтверждённого перехода к заводскому жёстко закреплённому кольцу на №5691 возврат сидушки не ожидается."]
  }
 },
 ORZ:{
  ambiguous1910:{
   years:[1910],
   _confidence:"low",
   _reason:"Для ОРЗ 1910 года полная конфигурация всё ещё известна плохо, но появились две прямые ранние точки по нижней части и хоботу.",
   skis:["Нижняя половина ОРЗ №201, 1910 подтверждает стальные лыжи на 19 отверстий с пропилами под ноги.","Верхняя часть этого станка тульская и не должна использоваться для реконструкции ОРЗ №201."],
   legs:["Пропилы под ноги на ОРЗ №201 есть; точное исполнение самих ног по этому комплекту не используем."],
   table:["Стол стальной; точная ранняя вариация ОРЗ по 1910 году пока не установлена."],
   trunk:["Хобот ОРЗ №275, 1910 — стальной, с ранними фигурными лапками и проточками."],
   trunkLock:["Ожидается сложный подпружиненный механизм раннего типа."],
   axle:["Ожидается ранняя фигурная ось с проточками."],
   swivel:["Стальной ранний вертлюг; точное исполнение по 1910 году пока не закреплено чистой номерной серией."],
   clamp:["Ранний стальной хомут; конкретную форму по 1910 году лучше подтверждать по отдельной детали."],
   rearRuler:["Точная конфигурация ОРЗ по 1910 году пока не установлена."],
   coarse:["Ранний стальной механизм грубой наводки: 5 позиций, с прорезью/полупрорезью."],
   link:["Стальная связь («майка»)."],
   seat:["Надёжной границы по сидушке/катушке для раннего ОРЗ пока нет."]
  },
  steelPeriod:{
   years:[1911,1912,1913,1914],
   _confidence:"medium",
   _reason:"Это ожидаемая конфигурация периода по известным ОРЗ-экземплярам и номерным деталям; для конкретного станка нужна проверка по номеру.",
   skis:["Стальные лыжи.","Пропил под ноги присутствует."],
   legs:["Стальные ноги присутствуют."],
   table:["Стальной стол.","Боковые вырезы под нажимы присутствуют.","Посадочное место под заднюю радиусную линейку есть, но у ОРЗ оно выражено значительно слабее, чем у ТОЗ; его форма остаётся стабильной вплоть до исчезновения."],
   trunk:["Стальной хобот ранней схемы.","Фигурные проточки на лапках подтверждены, в том числе на ОРЗ №2061, 1914."],
   trunkLock:["Сложный подпружиненный механизм раннего типа.","Ручка раннего исполнения — полая."],
   axle:["Ранняя фигурная боевая ось с проточками."],
   swivel:["Стальной вертлюг.","Справа крупно выгравирован год, слева — «ОР.З.»; небольшой номер расположен сверху."],
   clamp:["Стальной хомут с «рогами»; ОРЗ №784, 1911 — прямая точка.","Для ОРЗ характерна ромбическая насечка флажка."],
   rearRuler:["Отдельная задняя радиусная линейка присутствует.","У ОРЗ нет дополнительного центрального отверстия под крепление квадратика, характерного для тульских линеек."],
   coarse:["Стальной ранний механизм: 5 позиций, с прорезью/полупрорезью."],
   link:["Стальная связь («майка»).","Номер — на одной стороне, год — на другой."],
   seat:["Сидушка/катушка относится к ранней штатной схеме, но её сохранность на дошедших экземплярах плохая."]
  },
  transition1915:{
   years:[1915],
   _confidence:"low",
   _reason:"1915 год переходный; материал и исполнение разных узлов меняются внутри серийного диапазона и не синхронно.",
   skis:["Стальные лыжи; пропил под ноги ещё встречается."],
   legs:["Ноги ещё используются."],
   table:["Переход к бронзовым столам уже произошёл не позднее №2758/3270.","На №3270 сохраняются и боковые вырезы под нажимы, и слабовыраженная посадка задней линейки."],
   trunk:["Граница перехода от стальных фигурных лапок к бронзовым упрощённым внутри 1915 года пока не закрыта."],
   trunkLock:["На №2599 ещё стоит сложный ранний механизм с полой ручкой."],
   axle:["Фигурная ранняя ось ещё характерна для ранней части периода."],
   swivel:["Вероятно стальной №2557 сменяется уже точно бронзовым №2758.","Бронзовые №2758/3270 рассчитаны на раннюю грубую наводку «5 позиций, с полупрорезью»; №4615 отличается только зоной сопряжения и уже рассчитан на переходный механизм «5 отверстий»."],
   clamp:["Хомут с «рогами» и задней радиусной линейкой ещё применяется; №2758 — прямая точка."],
   rearRuler:["Задняя радиусная линейка ещё присутствует; на №3270 посадка под неё сохраняется.","У ОРЗ дополнительного центрального отверстия под крепление квадратика на линейке нет."],
   coarse:["Ранний стальной 5-позиционный механизм подтверждён на №2458 и №3979.","Позднее появляется редкая переходная бронзовая ветвь с 5 отверстиями — это другой тип, не путать с «5 позициями»."],
   link:["Связь ранней части периода стальная."],
   seat:["По переходу сидушки/катушки у ОРЗ надёжных номерных границ пока недостаточно."]
  },
  late1916:{
   years:[1916],
   _confidence:"medium",
   _reason:"Поздняя конфигурация ОРЗ по нескольким узлам уже подтверждается серией номерных точек, хотя некоторые границы остаются широкими.",
   skis:["Поздние лыжи остаются стальными и тонкими.","Для подтверждённых экземпляров характерны 8 отверстий, отсутствие пропила под ноги и технологическое отверстие спереди."],
   legs:["К №4681 пропил под ноги уже исчезает; поздние ОРЗ идут без ног."],
   table:["Стол бронзовый.","На поздних комплектах посадка задней линейки уже исчезает, но боковые вырезы под нажимы продолжают жить дольше; прямой компонентный якорь этого состояния — стол №5551 с клеймом 1917."],
   trunk:["На №4681 и №5429 лапки уже бронзовые и без ранних фигурных проточек; у №5429 есть небольшая выборка."],
   trunkLock:["К №4681 механизм уже упрощённый; №5429 подтверждает то же состояние."],
   axle:["Поздняя ось округлая, упрощённая.","Выборка под стопор стола небольшая."],
   swivel:["Бронзовый вертлюг; поздняя форма сохраняет общую геометрию, изменяется сопряжение с типом грубой наводки."],
   clamp:["К №4681 хомут уже без «рогов»."],
   rearRuler:["На известных поздних комплектах посадка задней линейки уже отсутствует; для №4681 сам стол не доказано родной, поэтому твёрдая компонентная точка — №5551, 1917."],
   coarse:["№5151 подтверждает редкий бронзовый переходный механизм с 5 отверстиями; более поздняя ОРЗ-схема пока документирована недостаточно."],
   link:["К №5429 известна бронзовая связь («майка»)."],
   seat:["По штатному переходу сидушки/катушки у позднего ОРЗ данных пока недостаточно; финские переделки особенно мешают этому признаку."]
  },
  late1917:{
   years:[1917],
   _confidence:"medium",
   _reason:"Для пограничных станков детали нужно датировать по собственным клеймам. ОРЗ №5551 особенно важен: его стол и хомут имеют 1917 год, а лапки хобота — 1916.",
   skis:["Наиболее вероятно сохраняются поздние тонкие стальные лыжи на 8 отверстий без пропила под ноги; такая схема устойчива уже на поздних ОРЗ 1916 года."],
   legs:["Ноги отсутствуют; пропил под них на поздних лыжах уже исчез."],
   table:["Боковые вырезы под нажимы ещё сохраняются.","Посадочного места под заднюю радиусную линейку уже нет; это прямо подтверждено столом ОРЗ №5551 с клеймом 1917."],
   trunk:["Поздние бронзовые лапки без фигурных проточек; на №5551 сами лапки имеют клеймо 1916, что показывает сборку на границе лет."],
   trunkLock:["Упрощённый механизм блокировки хобота; это состояние подтверждено на №4681, №5429 и переходном комплекте №5551."],
   axle:["Округлая упрощённая ось; для ОРЗ характерна небольшая выборка под стопор стола."],
   swivel:["Бронзовый поздний вертлюг."],
   clamp:["Поздний хомут без «рогов»; хомут №5551 имеет прямое клеймо 1917 года."],
   rearRuler:["Задняя радиусная линейка и её посадка отсутствуют; стол №5551, 1917 — прямая точка."],
   coarse:["Поздняя ОРЗ-конфигурация после переходного варианта на 5 отверстий пока изучена недостаточно."],
   link:["Бронзовая связь («майка») ожидается как продолжение поздней схемы, подтверждённой №5429, 1916."],
   seat:["По штатной поздней схеме сидушки/катушки ОРЗ данных недостаточно для жёсткой реконструкции."]
  }
 }
};

const POINTS = {
 TOZ:{
  skis:[
   {n:4541,y:null,f:["Стальные лыжи.","Утолщённое, уже более грубое исполнение по сравнению с ранними тонкими лыжами.","Пропилы под ноги есть.","Число отверстий по фото читается неуверенно: 15 или 16; точное значение пока не фиксируем."],c:"high",label:"ТОЗ №4541 · переходное исполнение лыж",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["Стальные лыжи.","Тонкое раннее исполнение.","Пропилы под ноги есть.","Число отверстий по фото читается неуверенно: 15 или 16; точное значение пока не фиксируем."],c:"high",label:"ТОЗ №3920 · 1915",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Стальные лыжи.","Тонкое раннее исполнение.","Пропилы под ноги есть.","Число отверстий по фото читается неуверенно: 15 или 16; точное значение пока не фиксируем."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:2490,y:null,f:["Стальные лыжи.","Тонкое раннее исполнение.","Пропилы под ноги есть.","Заметных отличий от обычной ранней тульской схемы не выявлено."],c:"high",label:"ТОЗ №2490 · ~1914–1915",url:"https://maximgun.ru/products/13"},
   {n:1280,y:1912,f:["Стальные лыжи.","Тонкое раннее исполнение.","Пропилы под ноги есть.","Число отверстий по имеющимся фото читается неуверенно: 15 или 16; одно отверстие закрыто колесом, поэтому точное значение пока не фиксируем."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:1928,y:1914,f:["Стальные лыжи.","Тонкое исполнение.","Пропилы под ноги есть.","Число отверстий по имеющимся фото читается неуверенно: 15 или 16; точное значение пока не фиксируем."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:792,y:1911,f:["Стальные лыжи.","Тонкое раннее исполнение.","19 отверстий.","Пропилы под ноги есть."],c:"high",label:"ТОЗ №792 · 1911",url:"https://maximgun.ru/details/340"},
   {n:635,y:1911,f:["Стальные лыжи.","19 отверстий.","Пропил под ноги есть."],c:"high",label:"ТОЗ №635 · 1911",url:"https://maximgun.ru/products/7"},
   {n:1216,y:1912,f:["Стальные лыжи.","15 отверстий.","Пропил под ноги есть."],c:"high",label:"ТОЗ №1216 · 1912",url:"https://maximgun.ru/products/332"},
   {n:5691,y:1916,f:["Стальные лыжи.","16 отверстий.","Пропилы под ноги есть.","Лыжи уже утолщённые и грубо обработанные."],c:"high",label:"ТОЗ №5691 · 1916",url:"https://maximgun.ru/products/488"},
   {n:6276,y:1916,f:["Стальные лыжи.","16 отверстий.","Пропил под ноги есть.","По бокам заметны утолщения из-за сокращения последующей обработки."],c:"high",label:"ТОЗ №6276 · 1916",url:"https://maximgun.ru/details/505"},
   {n:10137,y:1916,f:["Бронзовые лыжи.","6 отверстий.","Пропил под ноги ещё есть."],c:"high",label:"ТОЗ №10137 · 1916",url:"https://maximgun.ru/search?searchType=all&q=10137"},
   {n:10679,y:1916,f:["Бронзовые лыжи.","6 отверстий.","Пропила под ноги уже нет."],c:"high",carry:true,label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/products/333"}
  ],
  legs:[
   {n:4541,y:null,f:["Ноги на сохранившемся экземпляре присутствуют.","Лыжи имеют штатные пропилы под ноги."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["На сохранившемся экземпляре ноги отсутствуют.","Пропилы под ноги на лыжах есть, поэтому отсутствие самих ног не считаем доказательством заводской схемы без ног."],c:"high",boundary:false,label:"ТОЗ №3920 · 1915 · ноги не сохранились",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Родные ноги на сохранившемся экземпляре присутствуют.","Лыжи имеют штатные пропилы под ноги."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:1280,y:1912,f:["Ноги на сохранившемся экземпляре присутствуют.","Лыжи имеют штатные пропилы под ноги."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:5691,y:1916,f:["Пропилы под ноги на лыжах есть.","Ноги штатно предусматривались; на сохранившемся финском экземпляре они сняты."],c:"high",label:"ТОЗ №5691 · 1916",url:"https://maximgun.ru/products/488"},
   {n:6276,y:1916,f:["Пропилы под ноги на лыжах есть.","По пропилам ноги относятся к штатной заводской конфигурации."],c:"high",label:"ТОЗ №6276 · 1916",url:"https://maximgun.ru/details/505"},
   {n:8037,y:1916,f:["Ноги присутствуют."],c:"high",label:"ТОЗ №8037 · 1916",url:"https://maximgun.ru/products/15"},
   {n:9122,y:1916,f:["Ноги присутствуют."],c:"high",label:"ТОЗ №9122 · 1916",url:"https://maximgun.ru/products/582"},
   {n:10137,y:1916,f:["Ноги ещё присутствуют."],c:"high",label:"ТОЗ №10137 · 1916",url:"https://maximgun.ru/search?searchType=all&q=10137"},
   {n:10679,y:1916,f:["Ног уже нет."],c:"high",carry:true,label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/products/333"}
  ],
  table:[
   {n:4541,y:null,f:["Бронзовый стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:2490,y:null,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №2490 · ~1914–1915",url:"https://maximgun.ru/products/13"},
   {n:1280,y:1912,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №635 · 1911",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:1168,y:1912,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №1168 · 1912",url:"https://maximgun.ru/details/249"},
   {n:1245,y:1912,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №1245 · 1912",url:"https://maximgun.ru/details/241"},
   {n:1543,y:1913,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №1543 · 1913",url:"https://maximgun.ru/details/24"},
   {n:2531,y:null,f:["Стальной стол; один из наиболее поздних известных стальных тульских столов."],c:"medium",label:"ТОЗ №2531",url:"https://maximgun.ru/details/281"},
   {n:3681,y:1915,f:["Бронзовый стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:3920,y:1915,f:["Бронзовый стол.","Боковые вырезы под нажимы есть.","Выраженная тульская выборка/опора под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №3920 · 1915",url:"https://maximgun.ru/products/8"},
   {n:4330,y:1915,f:["Бронзовый стол.","Боковые вырезы под нажимы есть.","Посадочное место под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №4330 · 1915",url:"https://maximgun.ru/details/217"},
   {n:5844,y:1916,f:["Бронзовый стол.","Боковые вырезы под нажимы есть."],c:"high",label:"ТОЗ №5844 · 1916",url:"https://maximgun.ru/products/587"},
   {n:8037,y:1916,f:["Посадочное место под заднюю радиусную линейку ещё есть.","Боковых вырезов под нажимы уже нет.","Пенёк под уровень есть, но выреза/посадки под сам уровень уже нет."],c:"high",label:"ТОЗ №8037 · 1916",url:"https://maximgun.ru/products/15"},
   {n:9326,y:1916,f:["Боковых вырезов под нажимы нет.","Посадочного места под заднюю радиусную линейку уже нет."],c:"high",carry:true,label:"ТОЗ №9326 · 1916",url:"https://maximgun.ru/details/99"},
   {n:10679,y:1916,f:["Поздний стол.","Посадочного места под заднюю радиусную линейку нет.","Пенёк обычный, без выреза под уровень."],c:"high",label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/details/135"},
   {n:16253,y:1917,f:["Поздний стол.","Боковых вырезов под нажимы нет.","Посадочного места под заднюю радиусную линейку нет."],c:"high",carry:true,label:"ТОЗ №16253 · 1917",url:"https://maximgun.ru/search?searchType=all&q=16253"}
  ],
  trunk:[
   {n:4541,y:null,f:["Лапки хобота стальные.","Фигурных боковых проточек/выборок уже нет."],c:"high",label:"ТОЗ №4541 · переходное исполнение лапок",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["Лапки стальные.","Ранние фигурные боковые выборки/проточки присутствуют."],c:"high",label:"ТОЗ №3920 · 1915",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Лапки стальные.","Ранние фигурные боковые выборки/проточки присутствуют."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:2490,y:null,f:["Лапки стальные.","Ранние фигурные боковые выборки/проточки присутствуют."],c:"high",label:"ТОЗ №2490 · ~1914–1915",url:"https://maximgun.ru/products/13"},
   {n:1280,y:1912,f:["Лапки стальные.","Ранние фигурные боковые выборки/проточки присутствуют."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Лапки стальные.","Ранние фигурные боковые выборки/проточки присутствуют."],c:"high",label:"ТОЗ №635 · 1911",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Лапки стальные.","Ранние фигурные боковые выборки/проточки присутствуют."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:4419,y:1915,f:["Лапки стальные.","Боковых фигурных выборок/проточек на лапках нет.","Механизм блокировки не сохранился; по его типу эту деталь использовать нельзя."],c:"high",label:"ТОЗ №4419 · ~1915",url:"https://maximgun.ru/details/352"},
   {n:5691,y:1916,f:["Родной хобот сохранился.","Лапки стальные.","Фигурных проточек на лапках уже нет."],c:"high",label:"ТОЗ №5691 · 1916",url:"https://maximgun.ru/products/488"},
   {n:6207,y:1916,f:["Лапки стальные.","Фигурных проточек на лапках уже нет."],c:"high",label:"ТОЗ №6207 · 1916",url:"https://maximgun.ru/search?searchType=all&q=6207"},
   {n:6799,y:1916,f:["На известном комплекте встречаются бронзовые лапки с фигурными проточками.","Не доказано, что все детали комплекта относятся к одному номеру."],c:"low",boundary:false,label:"комплект с №6799",url:"https://maximgun.ru/search?searchType=all&q=6799"},
   {n:8037,y:1916,f:["Лапки стальные, без фигурных проточек."],c:"high",label:"ТОЗ №8037 · 1916",url:"https://maximgun.ru/products/15"},
   {n:9122,y:1916,f:["Лапки стальные, без фигурных проточек."],c:"high",label:"ТОЗ №9122 · 1916",url:"https://maximgun.ru/products/582"},
   {n:10679,y:1916,f:["Лапки стальные, без фигурных проточек."],c:"high",carry:true,label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/products/333"}
  ],
  trunkLock:[
   {n:4541,y:null,f:["Сложный подпружиненный механизм ранней схемы.","Ручка полая."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["Механизм блокировки необычного исполнения: элемент/ручка в виде «плавничка».","Возможно ремонтное исполнение; заводское происхождение не подтверждено.","Как серийную границу или типичное состояние ТОЗ 1915 года не использовать."],c:"medium",boundary:false,label:"ТОЗ №3920 · 1915 · необычная блокировка",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Сложный подпружиненный механизм ранней схемы.","Ручка полая."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:2490,y:null,f:["Сложный подпружиненный механизм ранней схемы.","Ручка полая."],c:"high",label:"ТОЗ №2490 · ~1914–1915",url:"https://maximgun.ru/products/13"},
   {n:1280,y:1912,f:["Сложный подпружиненный механизм ранней схемы.","Ручка полая."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Сложный подпружиненный механизм ранней схемы.","Ручка полая."],c:"high",label:"ТОЗ №635 · 1911",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Сложный подпружиненный механизм ранней схемы.","Ручка полая."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:4419,y:1915,f:["Механизм блокировки хобота не сохранился.","Экземпляр нельзя использовать как прямую номерную точку по типу механизма блокировки."],c:"high",boundary:false,label:"ТОЗ №4419 · ~1915 · механизм утрачен",url:"https://maximgun.ru/details/352"},
   {n:5691,y:1916,f:["Сложный подпружиненный механизм ещё сохраняется.","Ручка уже сплошная, не полая."],c:"high",label:"ТОЗ №5691 · 1916",url:"https://maximgun.ru/products/488"},
   {n:6207,y:1916,f:["Сложный подпружиненный механизм.","Ручка уже сплошная, не полая."],c:"high",label:"ТОЗ №6207 · 1916",url:"https://maximgun.ru/details/339"},
   {n:6799,y:1916,f:["Механизм ещё сложный.","Ручка уже сплошная."],c:"medium",label:"ТОЗ №6799 · 1916",url:"https://maximgun.ru/details/558"},
   {n:8037,y:1916,f:["Механизм уже упрощённый."],c:"high",label:"ТОЗ №8037 · 1916",url:"https://maximgun.ru/products/15"},
   {n:9122,y:1916,f:["Упрощённый механизм."],c:"high",carry:true,label:"ТОЗ №9122 · 1916",url:"https://maximgun.ru/products/582"}
  ],
  axle:[
   {n:4541,y:null,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ТОЗ №3920 · 1915",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:2490,y:null,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ТОЗ №2490 · ~1914–1915",url:"https://maximgun.ru/products/13"},
   {n:1280,y:1912,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ТОЗ №635 · 1911",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:4084,y:1915,f:["Ранняя фигурная ось с проточками."],c:"high",label:"ТОЗ №4084 · 1915",url:"https://maximgun.ru/details/240"},
   {n:5691,y:1916,f:["Ранняя фигурная боевая ось с фигурными проточками ещё сохраняется."],c:"high",label:"ТОЗ №5691 · 1916",url:"https://maximgun.ru/products/488"},
   {n:9122,y:1916,f:["Поздняя округлая упрощённая ось."],c:"high",carry:true,label:"ТОЗ №9122 · 1916",url:"https://maximgun.ru/products/582"},
   {n:10101,y:1916,f:["Поздняя округлая упрощённая ось.","Выборка под стопор стола крупная.","Сам станок сборный; как прямую точку используем именно номерную боевую ось."],c:"high",label:"ТОЗ №10101 · 1916",url:"https://maximgun.ru/details/144"},
   {n:10679,y:1916,f:["Поздняя округлая упрощённая ось."],c:"high",carry:true,label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/products/333"}
  ],
  swivel:[
   {n:3920,y:1915,f:["Стальной вертлюг.","Боковые отверстия есть."],c:"high",label:"ТОЗ №3920 · 1915",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Стальной вертлюг.","Боковые отверстия есть.","Рассчитан на раннюю 5-позиционную грубую наводку."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:2490,y:null,f:["Стальной вертлюг.","Боковые отверстия есть."],c:"high",label:"ТОЗ №2490 · ~1914–1915",url:"https://maximgun.ru/products/13"},
   {n:1280,y:1912,f:["Стальной вертлюг.","Боковые отверстия есть."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Стальной вертлюг.","Боковые отверстия есть."],c:"high",label:"ТОЗ №635 · 1911",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Стальной вертлюг.","Боковые отверстия есть."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:2719,y:null,f:["Стальной вертлюг.","Боковые отверстия есть.","Рассчитан на раннюю 5-позиционную грубую наводку."],c:"high",label:"ТОЗ №2719",url:"https://maximgun.ru/details/88"},
   {n:3219,y:1915,f:["Стальной вертлюг."],c:"high",label:"ТОЗ №3219 · 1915",url:"https://maximgun.ru/details/233"},
   {n:4322,y:1915,f:["Стальной вертлюг."],c:"high",label:"ТОЗ №4322 · 1915",url:"https://maximgun.ru/details/218"},
   {n:4541,y:null,f:["Стальной вертлюг.","Боковые отверстия есть."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:5844,y:1916,f:["Стальной вертлюг.","Боковые отверстия есть."],c:"high",label:"ТОЗ №5844 · 1916",url:"https://maximgun.ru/products/587"},
   {n:6134,y:1916,f:["Бронзовый вертлюг.","Боковые отверстия ещё есть.","По форме от стального №4541 заметно отличается только материалом."],c:"high",label:"ТОЗ №6134 · 1916",url:"https://maximgun.ru/details/238"},
   {n:6612,y:1916,f:["Бронзовый вертлюг.","Боковые отверстия ещё есть."],c:"high",label:"ТОЗ №6612 · 1916",url:"https://maximgun.ru/details/80"},
   {n:8037,y:1916,f:["Бронзовый вертлюг.","Боковых отверстий уже нет."],c:"high",label:"ТОЗ №8037 · 1916",url:"https://maximgun.ru/products/15"},
   {n:8367,y:1916,f:["Бронзовый вертлюг без боковых отверстий."],c:"high",label:"ТОЗ №8367 · 1916",url:"https://maximgun.ru/details/87"},
   {n:10679,y:1916,f:["Бронзовый вертлюг.","Боковых отверстий нет."],c:"high",carry:true,label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/details/133"},
   {n:11457,y:1916,f:["Бронзовый вертлюг.","Боковых отверстий нет.","Все известные более поздние царские ТОЗ сохраняют это исполнение."],c:"high",carry:true,label:"ТОЗ №11457 · 1916",url:"https://maximgun.ru/details/237"},
   {n:13743,y:1917,f:["Бронзовый вертлюг.","Боковых отверстий нет.","Вероятно рассчитан на позднюю грубую наводку на 3 отверстия, но это по самой детали не подтверждено."],c:"high",label:"ТОЗ №13743 · 1917",url:"https://maximgun.ru/details/23"},
   {n:14996,y:1917,f:["Бронзовый вертлюг.","Боковых отверстий нет.","Рассчитан на позднюю грубую наводку на 3 отверстия."],c:"high",label:"ТОЗ №14996 · 1917",url:"https://maximgun.ru/details/98"},
   {n:15319,y:1917,f:["Боковое отверстие есть только с одной стороны вертлюга.","Причина и назначение такого одностороннего отверстия пока неизвестны.","Неизученное аномальное исполнение; не использовать как границу обычной эволюции конструкции."],c:"high",boundary:false,label:"ТОЗ №15319 · 1917 · аномальное исполнение",url:"https://maximgun.ru/details/100"},
   {n:18033,y:1917,f:["Бронзовый вертлюг.","Боковых отверстий нет."],c:"high",label:"ТОЗ №18033 · 1917",url:"https://maximgun.ru/details/22"}
  ],
  clamp:[
   {n:297,y:null,f:["Ранний стальной хомут конструкции образца 1909 года.","«Рогов» под отдельную заднюю радиусную линейку нет.","В базе год оценён ориентировочно как 1910."],c:"high",label:"ТОЗ №297 · ~1910",url:"https://maximgun.ru/details/285"},
   {n:1928,y:1914,f:["Стальной хомут.","«Рога» под заднюю радиусную линейку есть.","Родная номерная деталь ТОЗ №1928."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/details/48"},
   {n:3219,y:1915,f:["Стальной хомут.","«Рога» под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №3219 · 1915",url:"https://maximgun.ru/details/508"},
   {n:4541,y:null,f:["Родной хомут.","«Рога» под заднюю радиусную линейку есть."],c:"high",label:"ТОЗ №4541 · хомут с рогами",url:"https://maximgun.ru/products/9"},
   {n:6134,y:1916,f:["Хомут ещё с «рогами» под заднюю радиусную линейку."],c:"high",label:"ТОЗ №6134 · 1916",url:"https://maximgun.ru/details/282"},
   {n:8037,y:1916,f:["Родной хомут не сохранился.","По сохранившейся посадке задней радиусной линейки на столе исходный хомут должен был быть с «рогами»."],c:"medium",boundary:false,label:"ТОЗ №8037 · реконструкция",url:"https://maximgun.ru/products/15"},
   {n:10679,y:1916,f:["Поздний хомут без «рогов»."],c:"high",carry:true,label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/products/333"},
   {n:14818,y:1917,f:["Поздний хомут без «рогов»."],c:"high",carry:true,label:"ТОЗ №14818 · 1917",url:"https://maximgun.ru/details/134"}
  ],
  rearRuler:[
   {n:4541,y:null,f:["Задняя радиусная линейка сохранилась."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["Задняя радиусная линейка не сохранилась.","Экземпляр нельзя использовать как прямую точку по исполнению самой линейки."],c:"high",boundary:false,label:"ТОЗ №3920 · 1915 · линейка утрачена",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Задняя радиусная линейка сохранилась.","Обычное тульское исполнение: есть дополнительное центральное отверстие для крепления квадратика."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:1280,y:1912,f:["Задняя радиусная линейка сохранилась и относится к штатной конфигурации экземпляра.","Обычное тульское исполнение: есть дополнительное центральное отверстие для крепления квадратика."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Задняя радиусная линейка сохранилась.","Обычное тульское исполнение: есть дополнительное центральное отверстие для крепления квадратика."],c:"high",label:"ТОЗ №635 · 1911",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Задняя радиусная линейка присутствует.","Обычное тульское исполнение: есть дополнительное центральное отверстие для крепления квадратика."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:2531,y:null,f:["Задняя радиусная линейка ТОЗ.","Есть дополнительное отверстие посередине для крепления квадратика.","Такое центральное отверстие характерно для тульских линеек; у ОРЗ его нет."],c:"high",label:"ТОЗ №2531 · задняя радиусная линейка",url:"https://maximgun.ru/details/369"},
   {n:8037,y:1916,f:["Посадочное место под заднюю радиусную линейку ещё сохраняется."],c:"high",label:"ТОЗ №8037 · 1916",url:"https://maximgun.ru/products/15"},
   {n:9326,y:1916,f:["Посадочного места под заднюю радиусную линейку уже нет."],c:"high",carry:true,label:"ТОЗ №9326 · 1916",url:"https://maximgun.ru/details/99"},
   {n:16253,y:1917,f:["Посадочного места под заднюю радиусную линейку нет."],c:"high",carry:true,label:"ТОЗ №16253 · 1917",url:"https://maximgun.ru/search?searchType=all&q=16253"}
  ],
  coarse:[
   {n:4541,y:null,f:["Родной ранний стальной механизм грубой наводки.","5 позиций, с прорезью."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["Родной механизм грубой наводки не сохранился.","Экземпляр нельзя использовать как прямую точку по типу грубой наводки."],c:"high",boundary:false,label:"ТОЗ №3920 · 1915 · узел утрачен",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Родной ранний стальной механизм грубой наводки.","5 позиций, с прорезью."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:1280,y:1912,f:["Родной ранний стальной механизм грубой наводки.","5 позиций, с прорезью."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:1928,y:1914,f:["Ранний стальной механизм грубой наводки: 5 позиций, с прорезью.","На сохранившемся узле имеются финские модификации; заводской тип при этом определяется."],c:"medium",boundary:false,label:"ТОЗ №1928 · 1914 · финские модификации",url:"https://maximgun.ru/products/2"},
   {n:807,y:null,f:["Стальной ранний механизм: 5 позиций, с прорезью.","Подтип прямо указан в базе."],c:"high",label:"ТОЗ №807 · ранняя грубая наводка",url:"https://maximgun.ru/details/256"},
   {n:974,y:null,f:["Стальной ранний механизм: 5 позиций, с прорезью.","Подтип прямо указан в базе."],c:"high",label:"ТОЗ №974 · ранняя грубая наводка",url:"https://maximgun.ru/details/362"},
   {n:3219,y:1915,f:["Стальной ранний механизм: 5 позиций, с прорезью.","Подтип прямо указан в базе."],c:"high",label:"ТОЗ №3219 · 1915",url:"https://maximgun.ru/details/368"},
   {n:3497,y:1915,f:["Стальной ранний механизм: 5 позиций, с прорезью.","Подтип прямо указан в базе."],c:"high",label:"ТОЗ №3497 · 1915",url:"https://maximgun.ru/details/367"},
   {n:3979,y:1915,f:["Стальной ранний механизм: 5 позиций, с прорезью.","Подтип прямо указан в базе."],c:"high",label:"ТОЗ №3979 · 1915",url:"https://maximgun.ru/details/364"},
   {n:4391,y:null,f:["Ранний механизм: 5 позиций, с прорезью.","В базе отмечена финская модернизация; как жёсткую серийную границу не используем.","Датировка ориентировочно около 1915 года."],c:"medium",boundary:false,label:"ТОЗ №4391 · ~1915 · финская модификация",url:"https://maximgun.ru/details/467"},
   {n:5349,y:null,f:["Стальной ранний механизм: 5 позиций, с прорезью.","Подтип прямо указан в базе."],c:"high",label:"ТОЗ №5349",url:"https://maximgun.ru/details/366"},
   {n:5099,y:null,f:["Стальной ранний механизм грубой наводки: 5 позиций, с прорезью.","Сейчас установлен на ТОЗ №635, 1911, но не является родным этому станку.","На механизме имеются финские модификации."],c:"medium",boundary:false,label:"ТОЗ №5099 · неродной на №635 · финские модификации",url:"https://maximgun.ru/products/7"},
   {n:6014,y:1916,f:["Стальной ранний механизм: 5 позиций, с прорезью."],c:"high",label:"ТОЗ №6014 · 1916",url:"https://maximgun.ru/details/365"},
   {n:6612,y:1916,f:["Стальной ранний 5-позиционный тип ещё сохраняется.","Есть финские вмешательства."],c:"medium",label:"ТОЗ №6612",url:"https://maximgun.ru/products/12"},
   {n:8037,y:1916,f:["Стальной поздний тип на 3 отверстия.","Как точную границу использовать осторожно: возможна ремонтная замена."],c:"medium",label:"ТОЗ №8037 · 1916",url:"https://maximgun.ru/products/15"},
   {n:8367,y:1916,f:["Стальной поздний тип на 3 отверстия; визуально выглядит штатно."],c:"medium",carry:true,label:"ТОЗ №8367 · 1916",url:"https://maximgun.ru/products/16"},
   {n:9122,y:1916,f:["Родной механизм грубой наводки не сохранился."],c:"low",boundary:false,label:"ТОЗ №9122 · узел утрачен",url:"https://maximgun.ru/products/582"},
   {n:10679,y:1916,f:["Родной механизм грубой наводки не сохранился.","По вертлюгу исходно должен был стоять поздний стальной вариант на 3 отверстия."],c:"medium",boundary:false,label:"ТОЗ №10679 · реконструкция",url:"https://maximgun.ru/products/333"}
  ],
  fine:[
   {n:1280,y:1912,f:["Механизм тонкой наводки сохранился, но клейм/номера на нём не видно.","Родность механизма конкретно станку №1280 подтвердить нельзя; как прямую номерную точку по типу тонкой наводки не используем."],c:"medium",boundary:false,label:"ТОЗ №1280 · 1912 · родность не подтверждена",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Родной механизм тонкой наводки не сохранился.","Экземпляр нельзя использовать как прямую точку по типу тонкой наводки."],c:"high",boundary:false,label:"ТОЗ №635 · 1911 · узел утрачен",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Родной механизм тонкой наводки не сохранился.","Экземпляр нельзя использовать как прямую точку по типу тонкой наводки."],c:"high",boundary:false,label:"ТОЗ №1928 · 1914 · узел утрачен",url:"https://maximgun.ru/products/2"},
   {n:1968,y:null,f:["Тонкая наводка со стальным внешним винтом.","В базе отнесена ко второму типу."],c:"high",label:"ТОЗ №1968",url:"https://maximgun.ru/details/511"},
   {n:6817,y:1916,f:["Тонкая наводка со стальным внешним винтом.","В базе отнесена ко второму типу."],c:"high",label:"ТОЗ №6817 · 1916",url:"https://maximgun.ru/details/509"},
   {n:9581,y:1916,f:["Поздний царский тип тонкой наводки.","Маховичок не сквозной."],c:"high",label:"ТОЗ №9581 · 1916",url:"https://maximgun.ru/details/537"},
   {n:13768,y:null,f:["Поздний царский тип тонкой наводки.","Маховичок не сквозной."],c:"high",label:"ТОЗ №13768",url:"https://maximgun.ru/details/534"}
  ],
  level:[
   {n:2490,y:null,f:["Обычный тульский уровень.","Заметных конструктивных отличий от других известных уровней не выявлено."],c:"high",boundary:false,label:"ТОЗ №2490 · уровень",url:"https://maximgun.ru/details/274"}
  ],
  link:[
   {n:4541,y:null,f:["Связь («майка») по внешнему виду, вероятно, стальная.","Материал пока подтверждён неуверенно; как границу перехода сталь → бронза эту точку не используем."],c:"medium",boundary:false,label:"ТОЗ №4541 · майка предположительно стальная",url:"https://maximgun.ru/products/9"},
   {n:3920,y:1915,f:["Связь («майка») стальная."],c:"high",label:"ТОЗ №3920 · 1915",url:"https://maximgun.ru/products/8"},
   {n:3681,y:1915,f:["Родная связь («майка») стальная."],c:"high",label:"ТОЗ №3681 · 1915",url:"https://maximgun.ru/products/482"},
   {n:1280,y:1912,f:["Родная связь («майка») стальная."],c:"high",label:"ТОЗ №1280 · 1912",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Стальная связь («майка»)."],c:"high",label:"ТОЗ №635 · 1911 · майка",url:"https://maximgun.ru/details/78"},
   {n:1928,y:1914,f:["Связь («майка») стальная."],c:"high",label:"ТОЗ №1928 · 1914",url:"https://maximgun.ru/products/2"},
   {n:792,y:1911,f:["Стальная связь («майка»)."],c:"high",label:"ТОЗ №792 · 1911",url:"https://maximgun.ru/details/77"},
   {n:3060,y:1915,f:["Стальная связь («майка»)."],c:"high",label:"ТОЗ №3060 · 1915",url:"https://maximgun.ru/details/235"},
   {n:5691,y:1916,f:["Родная связь («майка») сохранилась.","Бронзовая."],c:"high",carry:true,label:"ТОЗ №5691 · 1916",url:"https://maximgun.ru/products/488"},
   {n:6200,y:1916,f:["Бронзовая связь («майка»)."],c:"high",carry:true,label:"ТОЗ №6200 · 1916",url:"https://maximgun.ru/details/93"},
   {n:6241,y:1916,f:["Бронзовая связь («майка»)."],c:"high",label:"ТОЗ №6241 · 1916",url:"https://maximgun.ru/details/94"},
   {n:6612,y:1916,f:["Сохранившаяся связь («майка») не родная.","Экземпляр нельзя использовать как номерную точку по материалу или типу связи."],c:"high",boundary:false,label:"ТОЗ №6612 · 1916 · майка заменена",url:"https://maximgun.ru/search?searchType=all&q=6612"},
   {n:6799,y:1916,f:["Связь («майка») бронзовая."],c:"high",label:"ТОЗ №6799 · 1916",url:"https://maximgun.ru/details/95"},
   {n:8037,y:1916,f:["На сохранившейся связи («майке») номер не читается.","Экземпляр нельзя использовать как прямую номерную точку по материалу или типу связи."],c:"high",boundary:false,label:"ТОЗ №8037 · 1916 · номер майки не читается",url:"https://maximgun.ru/products/15"},
   {n:10137,y:1916,f:["Связь («майка») бронзовая."],c:"high",label:"ТОЗ №10137 · 1916",url:"https://maximgun.ru/details/91"},
   {n:10679,y:1916,f:["Связь («майка») бронзовая."],c:"high",label:"ТОЗ №10679 · 1916",url:"https://maximgun.ru/details/97"},
   {n:12213,y:1916,f:["Связь («майка») бронзовая."],c:"high",label:"ТОЗ №12213 · 1916",url:"https://maximgun.ru/details/117"}
  ],
  seat:[
   {n:3681,y:1915,f:["Сидушка/катушка на сохранившемся экземпляре не сохранилась.","Само отсутствие узла сегодня не используется как признак заводской конфигурации."],c:"high",boundary:false,label:"ТОЗ №3681 · 1915 · узел утрачен",url:"https://maximgun.ru/products/482"},
   {n:1280,y:1912,f:["Сидушка/катушка на сохранившемся экземпляре не сохранилась.","Само отсутствие узла сегодня не используется как признак заводской конфигурации."],c:"high",boundary:false,label:"ТОЗ №1280 · 1912 · узел утрачен",url:"https://maximgun.ru/products/530"},
   {n:635,y:1911,f:["Штатная сидушка/катушка на сохранившемся экземпляре не сохранилась.","Само отсутствие узла сегодня не используется как признак заводской конфигурации."],c:"high",boundary:false,label:"ТОЗ №635 · 1911 · узел утрачен",url:"https://maximgun.ru/products/7"},
   {n:1928,y:1914,f:["Штатная сидушка/катушка на сохранившемся экземпляре не сохранилась.","Само отсутствие узла сегодня не используется как признак заводской конфигурации."],c:"high",boundary:false,label:"ТОЗ №1928 · 1914 · узел утрачен",url:"https://maximgun.ru/products/2"},
   {n:4419,y:1915,f:["Сидушка/катушка не сохранилась.","По оценке, скорее всего её уже изначально не было, но это не подтверждено."],c:"low",boundary:false,label:"ТОЗ №4419 · неуверенно",url:"https://maximgun.ru/search?searchType=all&q=4419"},
   {n:4541,y:null,f:["Штатная сидушка/катушка ещё присутствует; считаем её родной для этого станка."],c:"high",label:"ТОЗ №4541",url:"https://maximgun.ru/products/9"},
   {n:5691,y:1916,f:["Сидушка конструктивно уже не предусмотрена.","Вместо неё стоит заводское жёстко закреплённое кольцо."],c:"high",carry:true,label:"ТОЗ №5691 · 1916",url:"https://maximgun.ru/products/488"}
  ],
  shieldSpool:[]
 },
 ORZ:{
  skis:[
   {n:1480,y:1912,f:["Стальные тонкие лыжи.","Пропилы под ноги есть.","Число отверстий по имеющимся фото не удаётся сосчитать; не фиксируется."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:862,y:1911,f:["Стальные ранние лыжи.","Пропилы под ноги есть.","Число отверстий по имеющимся фото не видно; не фиксируется."],c:"high",label:"ОРЗ №862 · 1911",url:"https://maximgun.ru/products/549"},
   {n:201,y:1910,f:["Нижняя половина станка действительно ОРЗ №201.","Лыжи стальные.","19 отверстий.","Пропилы под ноги есть.","Верхняя половина станка тульская и для атрибуции ОРЗ №201 не используется."],c:"high",label:"ОРЗ №201 · 1910 · нижняя часть",url:"https://maximgun.ru/products/485"},
   {n:2599,y:1915,f:["Стальные лыжи.","Пропил под ноги есть.","Отверстий больше поздних восьми."],c:"high",label:"ОРЗ №2599 · 1915",url:"https://maximgun.ru/details/545"},
   {n:2715,y:1915,f:["Стальные лыжи.","Пропил под ноги есть.","Отверстий больше 8."],c:"high",label:"ОРЗ №2715 · 1915",url:"https://maximgun.ru/details/13"},
   {n:4681,y:1916,f:["Стальные тонкие лыжи.","8 отверстий.","Пропила под ноги нет.","Спереди остаётся технологическое отверстие."],c:"high",carry:true,label:"ОРЗ №4681 · 1916",url:"https://maximgun.ru/search?searchType=all&q=4681"},
   {n:5429,y:1916,f:["Стальные тонкие лыжи.","8 отверстий.","Пропила под ноги нет.","Спереди остаётся технологическое отверстие."],c:"high",carry:true,label:"ОРЗ №5429 · 1916",url:"https://maximgun.ru/details/130"},
   {n:6385,y:1916,f:["Стальные тонкие лыжи.","8 отверстий.","Пропила под ноги нет.","Спереди остаётся технологическое отверстие."],c:"high",carry:true,label:"ОРЗ №6385 · 1916",url:"https://maximgun.ru/details/250"}
  ],
  legs:[
   {n:1480,y:1912,f:["Родные ноги сохранились."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:2599,y:1915,f:["Пропил под ноги ещё есть; ноги относятся к штатной конфигурации этого периода."],c:"medium",label:"ОРЗ №2599 · 1915",url:"https://maximgun.ru/details/545"},
   {n:4681,y:1916,f:["Пропил под ноги на лыжах уже исчез; поздняя схема идёт без ног."],c:"high",carry:true,label:"ОРЗ №4681 · 1916",url:"https://maximgun.ru/search?searchType=all&q=4681"},
   {n:5551,y:null,f:["На переходном комплекте ног нет; вырезов под них на лыжах нет."],c:"medium",boundary:false,label:"ОРЗ №5551 · комплект 1916–1917",url:"https://maximgun.ru/products/532"}
  ],
  table:[
   {n:1480,y:1912,f:["Стальной стол ранней схемы.","Боковые вырезы под нажимы есть.","Посадочное место под заднюю радиусную линейку есть и выражено слабее, чем у ТОЗ."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:862,y:1911,f:["Стальной стол ранней схемы.","Боковые вырезы под нажимы есть.","Посадочное место под заднюю радиусную линейку есть и выражено слабее, чем у ТОЗ."],c:"high",label:"ОРЗ №862 · 1911",url:"https://maximgun.ru/products/549"},
   {n:726,y:1911,f:["Стальной стол.","Боковые вырезы под нажимы есть.","Посадочное место под заднюю радиусную линейку есть, но выражено слабее, чем у ТОЗ."],c:"high",label:"ОРЗ №726 · 1911",url:"https://maximgun.ru/details/248"},
   {n:2758,y:1915,f:["Бронзовый стол."],c:"high",label:"ОРЗ №2758 · 1915",url:"https://maximgun.ru/details/125"},
   {n:3270,y:1915,f:["Бронзовый стол.","Боковые вырезы под нажимы есть.","Слабовыраженная посадка под заднюю радиусную линейку есть."],c:"high",label:"ОРЗ №3270 · 1915",url:"https://maximgun.ru/details/18"},
   {n:4173,y:1915,f:["Бронзовый стол.","Боковые вырезы под нажимы точно есть.","Посадочное место задней линейки по фото читается плохо; кажется, что его уже нет, но это не подтверждено."],c:"medium",label:"ОРЗ №4173 · 1915",url:"https://maximgun.ru/details/141"},
   {n:4681,y:1916,f:["На известном комплекте стол без посадочного места под заднюю радиусную линейку.","Не доказано, что этот стол родной номеру №4681."],c:"low",boundary:false,label:"комплект ОРЗ №4681",url:"https://maximgun.ru/search?searchType=all&q=4681"},
   {n:5337,y:1917,f:["Боковые вырезы под нажимы сохраняются.","Посадочного места под заднюю радиусную линейку уже нет.","Первая цифра номера читается неуверенно."],c:"low",boundary:false,label:"ОРЗ ~№5337 · номер неуверенный",url:"https://maximgun.ru/details/356"},
   {n:5551,y:1917,f:["Стол имеет клеймо 1917 года.","Посадки под заднюю линейку уже нет.","Боковые вырезы под нажимы ещё сохраняются."],c:"high",carry:true,label:"ОРЗ №5551 · стол · 1917",url:"https://maximgun.ru/details/542"}
  ],
  trunk:[
   {n:1480,y:1912,f:["Лапки хобота стальные.","Ранние фигурные боковые проточки/выборки есть."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:862,y:1911,f:["Лапки хобота стальные.","Ранние фигурные боковые проточки/выборки есть."],c:"high",label:"ОРЗ №862 · 1911",url:"https://maximgun.ru/products/549"},
   {n:275,y:1910,f:["Лапки стальные.","Ранние фигурные проточки есть."],c:"high",label:"ОРЗ №275 · 1910",url:"https://maximgun.ru/details/136"},
   {n:2061,y:1914,f:["Лапки стальные.","Фигурные проточки есть."],c:"high",label:"ОРЗ №2061 · 1914",url:"https://maximgun.ru/details/234"},
   {n:4681,y:1916,f:["Лапки бронзовые.","Фигурной проточки уже нет."],c:"high",carry:true,label:"ОРЗ №4681 · 1916",url:"https://maximgun.ru/details/242"},
   {n:5429,y:1916,f:["Лапки бронзовые.","Фигурных проточек нет.","Есть небольшая выборка."],c:"high",carry:true,label:"ОРЗ №5429 · 1916",url:"https://maximgun.ru/details/128"}
  ],
  trunkLock:[
   {n:1480,y:1912,f:["Ранний сложный подпружиненный механизм блокировки хобота.","Ручка полая."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:2599,y:1915,f:["Ранний сложный механизм блокировки хобота.","Ручка полая."],c:"high",label:"ОРЗ №2599 · 1915",url:"https://maximgun.ru/details/335"},
   {n:4681,y:1916,f:["Механизм блокировки хобота уже упрощённый."],c:"high",carry:true,label:"ОРЗ №4681 · 1916",url:"https://maximgun.ru/search?searchType=all&q=4681"},
   {n:5429,y:1916,f:["Упрощённый механизм блокировки хобота."],c:"high",carry:true,label:"ОРЗ №5429 · 1916",url:"https://maximgun.ru/search?searchType=all&q=5429"},
   {n:5551,y:null,f:["На переходном комплекте механизм блокировки хобота упрощённый."],c:"high",carry:true,label:"ОРЗ №5551 · комплект 1916–1917",url:"https://maximgun.ru/products/532"}
  ],
  axle:[
   {n:1480,y:1912,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:862,y:1911,f:["Ранняя фигурная боевая ось с проточками."],c:"high",label:"ОРЗ №862 · 1911",url:"https://maximgun.ru/products/549"},
   {n:5429,y:1916,f:["Поздняя округлая упрощённая ось.","Выборка под стопор стола маленькая."],c:"high",carry:true,label:"ОРЗ №5429 · 1916",url:"https://maximgun.ru/details/129"}
  ],
  swivel:[
   {n:1480,y:1912,f:["Стальной вертлюг ранней схемы ОРЗ.","Боковые отверстия есть."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:862,y:1911,f:["Стальной вертлюг ранней схемы ОРЗ.","Исполнение без поздних упрощений; типовая ранняя заводская конфигурация."],c:"high",label:"ОРЗ №862 · 1911",url:"https://maximgun.ru/products/549"},
   {n:2557,y:1915,f:["Скорее всего стальной вертлюг; материал по фото не подтверждён окончательно."],c:"medium",label:"ОРЗ №2557 · 1915",url:"https://maximgun.ru/details/448"},
   {n:2758,y:1915,f:["Бронзовый вертлюг.","Рассчитан на раннюю грубую наводку «5 позиций, с полупрорезью»."],c:"high",label:"ОРЗ №2758 · 1915",url:"https://maximgun.ru/details/123"},
   {n:3270,y:1915,f:["Бронзовый вертлюг.","Рассчитан на раннюю грубую наводку «5 позиций, с полупрорезью»."],c:"high",label:"ОРЗ №3270 · 1915",url:"https://maximgun.ru/details/124"},
   {n:4615,y:1915,f:["Бронзовый вертлюг.","Рассчитан уже на переходный механизм грубой наводки с 5 отверстиями.","От №2758/3270 заметно отличается только зоной сопряжения с грубой наводкой."],c:"high",label:"ОРЗ №4615 · 1915",url:"https://maximgun.ru/details/86"}
  ],
  clamp:[
   {n:1480,y:1912,f:["Стальной хомут.","«Рога» под отдельную заднюю радиусную линейку есть."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:862,y:1911,f:["Стальной хомут.","«Рога» под отдельную заднюю радиусную линейку есть.","Обычное раннее исполнение ОРЗ."],c:"high",label:"ОРЗ №862 · 1911",url:"https://maximgun.ru/products/549"},
   {n:784,y:1911,f:["Стальной хомут.","«Рога» под отдельную заднюю радиусную линейку есть."],c:"high",label:"ОРЗ №784 · 1911",url:"https://maximgun.ru/details/247"},
   {n:2758,y:1915,f:["Хомут с «рогами»; задняя радиусная линейка ещё используется."],c:"high",label:"ОРЗ №2758 · 1915",url:"https://maximgun.ru/details/126"},
   {n:4681,y:1916,f:["Хомут уже позднего типа без «рогов»."],c:"high",carry:true,label:"ОРЗ №4681 · 1916",url:"https://maximgun.ru/search?searchType=all&q=4681"},
   {n:5551,y:1917,f:["Хомут имеет клеймо 1917 года.","Поздний тип без «рогов»."],c:"high",carry:true,label:"ОРЗ №5551 · хомут · 1917",url:"https://maximgun.ru/details/355"}
  ],
  rearRuler:[
   {n:1480,y:1912,f:["Задняя радиусная линейка сохранилась."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:2758,y:1915,f:["Задняя радиусная линейка ещё используется.","Дополнительного центрального отверстия под крепление квадратика, характерного для ТОЗ, у ОРЗ нет."],c:"high",label:"ОРЗ №2758 · 1915",url:"https://maximgun.ru/details/126"},
   {n:4681,y:1916,f:["На установленном столе посадочного места под заднюю линейку уже нет, но принадлежность стола номеру №4681 не подтверждена."],c:"low",boundary:false,label:"комплект ОРЗ №4681",url:"https://maximgun.ru/search?searchType=all&q=4681"},
   {n:5551,y:1917,f:["Стол с клеймом 1917 года уже без посадочного места под заднюю линейку."],c:"high",carry:true,label:"ОРЗ №5551 · стол · 1917",url:"https://maximgun.ru/products/532"}
  ],
  coarse:[
   {n:1480,y:1912,f:["Родной ранний стальной механизм грубой наводки.","5 позиций, с прорезью/полупрорезью."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:2458,y:1915,f:["Ранний стальной механизм: 5 позиций, с прорезью/полупрорезью.","Есть финские модификации."],c:"medium",label:"ОРЗ №2458 · 1915",url:"https://maximgun.ru/details/81"},
   {n:3979,y:1915,f:["Стальной ранний механизм: 5 позиций, с прорезью/полупрорезью."],c:"high",label:"ОРЗ №3979 · 1915",url:"https://maximgun.ru/search?searchType=all&q=3979"},
   {n:4615,y:1915,f:["Сам вертлюг №4615 уже выполнен под переходный механизм грубой наводки с 5 отверстиями."],c:"high",boundary:false,label:"ОРЗ №4615 · вертлюг под 5 отверстий",url:"https://maximgun.ru/search?searchType=all&q=4615"},
   {n:5151,y:1916,f:["Редкий бронзовый переходный механизм с 5 отверстиями.","Это не то же самое, что ранний механизм «на 5 позиций»."],c:"high",label:"ОРЗ №5151 · 1916",url:"https://maximgun.ru/details/20"}
  ],
  fine:[],
  level:[],
  link:[
   {n:1480,y:1912,f:["Родная связь («майка») стальная."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"},
   {n:2225,y:1914,f:["Стальная связь («майка»)."],c:"high",label:"ОРЗ №2225 · 1914",url:"https://maximgun.ru/details/232"},
   {n:5429,y:1916,f:["Бронзовая связь («майка»)."],c:"high",carry:true,label:"ОРЗ №5429 · 1916",url:"https://maximgun.ru/details/127"}
  ],
  seat:[
   {n:1480,y:1912,f:["Штатная сидушка/катушка сохранилась."],c:"high",label:"ОРЗ №1480 · 1912",url:"https://maximgun.ru/products/554"}
  ],
  shieldSpool:[]
 }
};
const EXAMPLES = [
 {m:"TOZ",n:100,y:1909,title:"Станок обр. 1909 с ограничителями · Salzburg",desc:"Пример конструкции самого раннего образца; не используем карточку как точную датировку конкретного станка.",photoId:3209,img:"https://maximgun.ru/uploads/1755118757057_514b253a-7090-4407-b7ca-a5141ee3aed9.jpg",url:"https://maximgun.ru/products/331"},
 {m:"TOZ",n:297,y:1910,title:"Ранний хомут №297",desc:"Ранняя схема без «рогов»; отдельная деталь по конструкции образца 1909 года.",photoId:5779,img:"https://maximgun.ru/uploads/1765897888925_7bd2a6b2-c730-48d8-a964-e1619ac96181.jpg",url:"https://maximgun.ru/details/285"},
 {m:"TOZ",n:635,y:1911,title:"ТОЗ №635 · 1911",reference:true,desc:"Опорный ранний ТОЗ: стальные лыжи на 19 отверстий с пропилами под ноги; стальной стол с боковыми вырезами и выраженной тульской опорой под заднюю линейку; стальные фигурные лапки; сложная блокировка с полой ручкой; фигурная боевая ось; стальной вертлюг с боковыми отверстиями; стальная майка; штатная тульская задняя радиусная линейка с центральным отверстием под квадратик. Родная грубая наводка не сохранилась: сейчас установлен тульский механизм №5099 раннего типа на 5 позиций с прорезью, с финскими модификациями. Тонкая наводка и сидушка/катушка не сохранились.",photoId:184,img:"https://maximgun.ru/uploads/1753686226013_b0787fac-61fc-42d5-b9ed-908fd2860dd7.jpeg",url:"https://maximgun.ru/products/7"},
 {m:"TOZ",n:1216,y:1912,title:"ТОЗ №1216 · 1912",desc:"Номерная точка по лыжам: сталь, 15 отверстий, пропил под ноги.",photoId:3214,img:"https://maximgun.ru/uploads/1755120015359_51d342ac-79c3-40c6-a44f-21f8e9042ca8.jpeg",url:"https://maximgun.ru/products/332"},
 {m:"TOZ",n:1280,y:1912,title:"ТОЗ №1280 · 1912",reference:true,desc:"Опорный комплектный тульский станок 1912 года: тонкие стальные лыжи с пропилами под ноги (15–16 отверстий, точное число закрыто колесом), сохранившиеся ноги, стальной стол ранней тульской схемы, фигурные лапки и боевая ось, сложная блокировка с полой ручкой, стальной вертлюг с боковыми отверстиями, родная ранняя 5-позиционная грубая наводка, стальная майка и тульская задняя радиусная линейка. Тонкая наводка сохранилась, но её клейма не читаются, поэтому родность номеру не подтверждена; сидушка/катушка утрачена.",photoId:3443,img:"https://maximgun.ru/uploads/1755675386940_39c280e8-3b78-431f-88e1-bea5b316ac95.JPG",url:"https://maximgun.ru/products/530"},
 {m:"ORZ",n:1480,y:1912,title:"ОРЗ №1480 · 1912",reference:true,desc:"Опорный ранний комплектный ОРЗ 1912 года: тонкие стальные лыжи с пропилами под ноги (число отверстий по фото не сосчитать), сохранившиеся ноги, стальной стол с боковыми вырезами и слабовыраженной ОРЗ-посадкой под заднюю радиусную линейку, стальные фигурные лапки, сложная подпружиненная блокировка с полой ручкой, ранняя фигурная боевая ось, стальной вертлюг с боковыми отверстиями и стальной хомут с рогами. Родная грубая наводка ранняя стальная, на 5 позиций с прорезью/полупрорезью; майка стальная; задняя радиусная линейка и штатная сидушка/катушка сохранились.",photoId:7297,img:"https://maximgun.ru/uploads/1780269898535_2259ffc1-86c9-4eee-9153-9391f71fcb5d.jpg",url:"https://maximgun.ru/products/554"},
 {m:"TOZ",n:1928,y:1914,title:"ТОЗ №1928 · 1914",reference:true,desc:"Опорный тульский станок 1914 года с большим количеством родных/согласованных деталей. Подтверждены: тонкие стальные лыжи с пропилами под ноги (число отверстий по фото 15–16, точно не читается), стальной стол с боковыми вырезами и тульской опорой под заднюю линейку, стальные лапки с фигурными проточками, сложная блокировка с полой ручкой, фигурная боевая ось, стальной вертлюг с боковыми отверстиями, стальная майка и штатная тульская задняя радиусная линейка с центральным отверстием под квадратик. Ранняя 5-позиционная грубая наводка сохранилась с финскими модификациями; тонкая наводка и сидушка/катушка утрачены. Хомут №1928 — прямая номерная точка. Ноги на сохранившемся экземпляре копийные.",photoId:5407,img:"https://maximgun.ru/uploads/1761903761409_6722ff02-c224-4655-8d94-715008ffd5e8.jpeg",url:"https://maximgun.ru/products/2"},
 {m:"ORZ",n:862,y:1911,title:"ОРЗ №862 · 1911",reference:true,desc:"Опорный ранний ОРЗ: по лыжам, столу, хоботу/лапкам, боевой оси, вертлюгу и хомуту подтверждено классическое раннее стальное исполнение. Число отверстий в лыжах по имеющимся фото не видно. Механизм блокировки хобота также не виден, поэтому по нему №862 не используется как номерная точка. На этом экземпляре дальнейшие признаки не достраиваются автоматически.",photoId:null,img:null,url:"https://maximgun.ru/products/549"},
 {m:"TOZ",n:2490,y:null,title:"ТОЗ №2490 · 1914–1915",reference:true,desc:"Опорный тульский станок переходного периода. По внешнему виду подтверждена обычная ранняя тульская схема без заметных отличий: тонкие стальные лыжи с пропилами под ноги; стальной стол с боковыми вырезами и выраженной тульской опорой под заднюю линейку; стальные фигурные лапки; сложная блокировка с полой ручкой; фигурная боевая ось; стальной вертлюг с боковыми отверстиями. Уровень №2490 также известен как отдельная номерная деталь и заметных отличий от других тульских уровней не имеет.",photoId:null,img:null,url:"https://maximgun.ru/products/13"},
 {m:"TOZ",n:3681,y:1915,title:"ТОЗ №3681 · 1915",reference:true,desc:"Ключевой переходный тульский станок: стол уже бронзовый, но остальной подтверждённый комплект сохраняет раннюю схему — тонкие стальные лыжи с пропилами и 15–16 отверстиями, родные ноги, стальные фигурные лапки, сложная блокировка с полой ручкой, фигурная ось, стальной вертлюг с боковыми отверстиями, ранняя 5-позиционная грубая наводка, стальная майка и тульская задняя линейка.",photoId:null,img:null,url:"https://maximgun.ru/products/482"},
 {m:"TOZ",n:3920,y:1915,title:"ТОЗ №3920 · 1915",reference:true,desc:"Опорный переходный тульский станок 1915 года: бронзовый стол с ранней геометрией; тонкие стальные лыжи с пропилами; стальные фигурные лапки; фигурная ось; стальной вертлюг с боковыми отверстиями; стальная майка. Блокировка необычная, с элементом в виде «плавничка», возможно ремонтная; грубая наводка и задняя линейка не сохранились.",photoId:null,img:null,url:"https://maximgun.ru/products/8"},
 {m:"TOZ",n:4541,y:null,title:"ТОЗ №4541 · около 1915–1916",reference:true,desc:"Ключевой переходный тульский станок. Лыжи уже утолщённые и грубо обработанные, с пропилами под ноги; отверстий по фото 15–16. Ноги сохранились. Стол бронзовый, но ещё с ранней геометрией: боковые вырезы и посадка задней линейки есть. Стальные лапки хобота уже без фигурных проточек, при этом блокировка ещё сложная с полой ручкой, боевая ось фигурная, вертлюг стальной с боковыми отверстиями, родная грубая наводка ранняя 5-позиционная. Родной хомут ещё с «рогами» под заднюю радиусную линейку. Задняя линейка сохранилась, штатная сидушка присутствует. Майка выглядит стальной, но номер и год на ней не читаются, поэтому прямой номерной точкой по материалу она не считается. Тонкую наводку по этому экземпляру не учитываем.",photoId:null,img:null,url:"https://maximgun.ru/products/9"},
 {m:"TOZ",n:4084,y:1915,title:"ТОЗ №4084 · 1915",desc:"Номерная фигурная боевая ось с проточками — твёрдая ранняя точка перед переходом к округлой оси.",photoId:5445,img:"https://maximgun.ru/uploads/1762364794897_e7696675-b19c-4ffb-80d9-da47ffec6f55.jpg",url:"https://maximgun.ru/details/240"},
 {m:"ORZ",n:2758,y:1915,title:"ОРЗ №2758 · 1915",desc:"Бронзовый стол и вертлюг; хомут ещё с «рогами».",photoId:3151,img:"https://maximgun.ru/uploads/1754850683648_f36d5c44-e5d8-46ed-ac70-1f13a8a785b1.jpeg",url:"https://maximgun.ru/products/330"},
 {m:"ORZ",n:3270,y:1915,title:"ОРЗ №3270 · 1915",desc:"Бронзовый стол с ранней геометрией и бронзовый вертлюг под раннюю 5-позиционную грубую наводку.",photoId:29,img:"https://maximgun.ru/uploads/1752764920971_549aa099-6d34-4d8a-aced-f80623b56954.jpg",url:"https://maximgun.ru/details/18"},
 {m:"TOZ",n:5691,y:1916,title:"ТОЗ №5691 · 1916",desc:"Ключевая переходная точка: грубые утолщённые стальные лыжи на 16 отверстий с пропилами под ноги; стальные гладкие лапки; сложная блокировка уже со сплошной ручкой; фигурная ось; бронзовая «майка»; заводское кольцо вместо сидушки.",photoId:5458,img:"https://maximgun.ru/uploads/1762654184058_c32bea10-406b-41ff-9e19-cb3170551d99.jpeg",url:"https://maximgun.ru/products/488"},
 {m:"TOZ",n:8037,y:1916,title:"ТОЗ №8037 · 1916",desc:"Ключевой переходный экземпляр: стол уже без боковых вырезов, но ещё с посадкой задней линейки; вертлюг уже без боковых отверстий.",photoId:310,img:"https://maximgun.ru/uploads/1753910507233_18a87f76-29c8-40fb-80db-7db4f2917183.jpg",url:"https://maximgun.ru/products/15"},
 {m:"TOZ",n:10679,y:1916,title:"ТОЗ №10679 · 1916",desc:"Поздняя конфигурация: бронзовые лыжи на 6 отверстий без пропила под ноги, ног нет, поздний хомут и вертлюг.",photoId:3231,img:"https://maximgun.ru/uploads/1755191659815_58f1cdec-6b9d-46ba-815e-8e0ad5f0878a.JPG",url:"https://maximgun.ru/products/333"},
 {m:"ORZ",n:4681,y:1916,title:"ОРЗ №4681 · 1916",desc:"Поздние тонкие лыжи на 8 отверстий; бронзовые гладкие лапки, упрощённая блокировка хобота и хомут без «рогов».",photoId:5454,img:"https://maximgun.ru/uploads/1762546179211_2bfda064-98d1-453d-ba1a-2fd2f5f664cf.jpg",url:"https://maximgun.ru/details/242"},
 {m:"ORZ",n:5429,y:1916,title:"ОРЗ №5429 · 1916",desc:"Поздняя устойчивая схема: 8 отверстий на лыжах, бронзовые упрощённые лапки, округлая ось и бронзовая «майка».",photoId:3195,img:"https://maximgun.ru/uploads/1755017776675_e8939f67-70bc-46dd-a48b-94f99453e834.jpeg",url:"https://maximgun.ru/details/130"},
 {m:"ORZ",n:5551,y:1917,title:"ОРЗ №5551 · граница 1916–1917",desc:"Пограничный комплект: стол и хомут имеют клеймо 1917 года, лапки хобота — 1916.",photoId:6933,img:"https://maximgun.ru/uploads/1772445772042_146db6b9-6cbb-4871-8d22-64c7c6c8323f.JPG",url:"https://maximgun.ru/products/532"},
 {m:"TOZ",n:16253,y:1917,title:"ТОЗ №16253 · 1917",desc:"Поздний номерной стол уже без боковых вырезов под нажимы и без посадки задней радиусной линейки.",photoId:81,img:"https://maximgun.ru/uploads/1753647624259_d65b32ae-0b61-437b-bdc7-7465c63a26ac.jpg",url:"https://maximgun.ru/products/6"}
];

/*
  Фото для номерных точек хранятся ОТДЕЛЬНО от product.
  Ключ: производитель -> узел -> серийный номер.

  featured:
    - null  -> крупную случайную картинку не показываем;
    - id    -> этот photo.id показывается превью;
    - URL   -> можно указать URL вручную, если фото не заведено в таблице photo.

  photos:
    - здесь только кандидаты, относящиеся к конкретной детали/узлу;
    - для /details/* кандидаты собраны из связей detail_photos;
    - для точек, существующих только как product/search, массив намеренно пустой:
      вставь сюда выбранные хорошие кадры вручную.

  Пример ручной настройки:
  5691: {
    featured: "https://maximgun.ru/uploads/GOOD_FRAME.jpg",
    photos: [
      { url: "https://maximgun.ru/uploads/GOOD_FRAME.jpg" },
      { url: "https://maximgun.ru/uploads/SECOND_FRAME.jpg" },
    ],
  }
*/
const FEATURE_PHOTOS = {
  TOZ: {
    skis: {
      792: {
        featured: null,
        photos: [
          { id: 6269, url: "https://maximgun.ru/uploads/1768388012855_761eaff8-ecb3-443d-a31f-788eb2c6e8dd.webp" },
          { id: 6270, url: "https://maximgun.ru/uploads/1768388014157_1a0458a9-aae2-4469-a504-dafeb0c4d397.webp" },
          { id: 6271, url: "https://maximgun.ru/uploads/1768388015125_fd016747-9321-4609-92f4-68ac7dc381c6.webp" },
          { id: 6272, url: "https://maximgun.ru/uploads/1768388016151_cdd5311a-ec2a-4d5c-af53-d8c72dfe3083.webp" },
          { id: 6273, url: "https://maximgun.ru/uploads/1768388017035_7f29c481-026a-42df-b04c-ae4ba77a59d8.webp" },
          { id: 6274, url: "https://maximgun.ru/uploads/1768388017974_c87f2cd7-ce1b-4268-9ff4-4f073bfafe0a.webp" },
          { id: 6275, url: "https://maximgun.ru/uploads/1768388018781_6cf06c6b-7854-4588-8ce2-1aee91cf4930.webp" },
        ], // https://maximgun.ru/details/340
      },
      635: { featured: null, photos: [] },
      1216: { featured: null, photos: [] },
      5691: { featured: null, photos: [] },
      6276: {
        featured: null,
        photos: [
          { id: 7849, url: "https://maximgun.ru/uploads/1784186959222_267426a8-0784-4237-8c39-2a94a4fdfc6f.jpg" },
          { id: 7850, url: "https://maximgun.ru/uploads/1784186959645_0dfb59a1-56cf-4f37-8f01-24221687e34e.jpg" },
          { id: 7863, url: "https://maximgun.ru/uploads/1784560781028_b49b76e2-4f89-41c3-b91b-14562c780d6d.jpg" },
          { id: 7864, url: "https://maximgun.ru/uploads/1784560781206_e689364e-dc1e-44a5-830a-d2600831d89d.jpg" },
          { id: 7865, url: "https://maximgun.ru/uploads/1784560781449_8743ca64-4a52-4a32-8d6d-3704fed42b69.jpg" },
          { id: 7869, url: "https://maximgun.ru/uploads/1784566597298_fac181f7-b340-46c3-8cab-ee88c4a788b7.jpg" },
        ], // https://maximgun.ru/details/505
      },
      10137: { featured: null, photos: [] },
      10679: { featured: null, photos: [] },
    },
    legs: {
      5691: { featured: null, photos: [] },
      6276: {
        featured: null,
        photos: [
          { id: 7849, url: "https://maximgun.ru/uploads/1784186959222_267426a8-0784-4237-8c39-2a94a4fdfc6f.jpg" },
          { id: 7850, url: "https://maximgun.ru/uploads/1784186959645_0dfb59a1-56cf-4f37-8f01-24221687e34e.jpg" },
          { id: 7863, url: "https://maximgun.ru/uploads/1784560781028_b49b76e2-4f89-41c3-b91b-14562c780d6d.jpg" },
          { id: 7864, url: "https://maximgun.ru/uploads/1784560781206_e689364e-dc1e-44a5-830a-d2600831d89d.jpg" },
          { id: 7865, url: "https://maximgun.ru/uploads/1784560781449_8743ca64-4a52-4a32-8d6d-3704fed42b69.jpg" },
          { id: 7869, url: "https://maximgun.ru/uploads/1784566597298_fac181f7-b340-46c3-8cab-ee88c4a788b7.jpg" },
        ], // https://maximgun.ru/details/505
      },
      8037: { featured: null, photos: [] },
      9122: { featured: null, photos: [] },
      10137: { featured: null, photos: [] },
      10679: { featured: null, photos: [] },
    },
    table: {
      1168: {
        featured: null,
        photos: [
          { id: 5490, url: "https://maximgun.ru/uploads/1763113270306_936235dc-a8cb-4538-906b-cf0124b2cb03.jpg" },
          { id: 5491, url: "https://maximgun.ru/uploads/1763113270564_9d244ba3-5868-4b72-a60d-18e57c3c38c8.jpg" },
          { id: 5492, url: "https://maximgun.ru/uploads/1763113270817_61265bba-dfe1-4d2c-823f-ac715435f23c.jpg" },
          { id: 7944, url: "https://maximgun.ru/uploads/1784892420471_07528e7c-b360-436c-9a1d-1d86cefdfd48.jpg" },
          { id: 7945, url: "https://maximgun.ru/uploads/1784892424713_8456ef74-764f-4d27-a319-5f781a725250.jpg" },
          { id: 7946, url: "https://maximgun.ru/uploads/1784892479762_9810b1b7-e95e-4cf0-8e2a-617f51c12e3b.jpg" },
        ], // https://maximgun.ru/details/249
      },
      1245: {
        featured: null,
        photos: [
          { id: 5450, url: "https://maximgun.ru/uploads/1762545907627_ca30030d-e629-4e08-9c35-cc1fc1b9b1c5.jpg" },
          { id: 5451, url: "https://maximgun.ru/uploads/1762545911679_14b3e6b7-b97d-40f0-b3cb-ed49504efd17.jpg" },
          { id: 5452, url: "https://maximgun.ru/uploads/1762545915616_10e15008-6a8d-449f-9259-44d7e5a8d97d.jpg" },
          { id: 5453, url: "https://maximgun.ru/uploads/1762545919501_0873ea2c-bee2-433f-b73f-22ffacba1148.jpg" },
          { id: 7905, url: "https://maximgun.ru/uploads/1784889906378_acc3f457-d9af-4ced-92a9-6304f7e220fb.jpg" },
          { id: 7907, url: "https://maximgun.ru/uploads/1784889916288_21e8eb84-82f2-4b0b-80cc-59d222a9fd54.jpg" },
          { id: 7900, url: "https://maximgun.ru/uploads/1784889872292_b3a3f1ae-f838-4fe1-b888-a7afc15baafd.jpg" },
          { id: 7908, url: "https://maximgun.ru/uploads/1784889921403_facdfd14-d735-4805-8c68-6cacdfd1084e.jpg" },
          { id: 7909, url: "https://maximgun.ru/uploads/1784889926579_56526125-b721-470b-b0e5-40eab475c197.jpg" },
          { id: 7895, url: "https://maximgun.ru/uploads/1784889835228_b56415c7-3a7d-4000-981d-345e375e0d0e.jpg" },
          { id: 7898, url: "https://maximgun.ru/uploads/1784889856942_12315fc7-5ace-4778-9555-fbc0d361898c.jpg" },
          { id: 7899, url: "https://maximgun.ru/uploads/1784889862585_469dc76d-f4ec-44c9-b243-53e0279b54f7.jpg" },
          { id: 7906, url: "https://maximgun.ru/uploads/1784889911080_be436786-af32-4ab7-9d9d-d172af26818a.jpg" },
          { id: 7896, url: "https://maximgun.ru/uploads/1784889843818_702f6a3b-d4ea-4a11-a0f1-9a7e0d452485.jpg" },
          { id: 7897, url: "https://maximgun.ru/uploads/1784889851327_18d4688b-9ac5-4a54-8118-0d3363b7db4d.jpg" },
          { id: 7901, url: "https://maximgun.ru/uploads/1784889882769_cd06c256-b240-4e19-8ae0-14ab574b20a2.jpg" },
          { id: 7902, url: "https://maximgun.ru/uploads/1784889888306_0949741d-858b-4a24-9583-c47139509d67.jpg" },
          { id: 7904, url: "https://maximgun.ru/uploads/1784889898794_f02a5c46-27bc-4c21-a1d0-ed4788b122a4.jpg" },
          { id: 7903, url: "https://maximgun.ru/uploads/1784889893493_e69604f7-35e9-4597-a71c-d7e0775104bc.jpg" },
        ], // https://maximgun.ru/details/241
      },
      1543: {
        featured: null,
        photos: [
          { id: 38, url: "https://maximgun.ru/uploads/1753220589777_efe7bdae-0192-4579-a71e-f9f4f2791d9c.webp" },
          { id: 39, url: "https://maximgun.ru/uploads/1753220598094_fd2d895c-2966-42d0-94a3-a6a42bf6f72b.png" },
        ], // https://maximgun.ru/details/24
      },
      2531: {
        featured: null,
        photos: [
          { id: 5752, url: "https://maximgun.ru/uploads/1765894168579_8082e598-7e71-4431-bdc2-4145a7d8d1b1.jpg" },
          { id: 5753, url: "https://maximgun.ru/uploads/1765894169633_dd6a89f4-92e4-426d-aece-07b85913fcc6.jpg" },
          { id: 5695, url: "https://maximgun.ru/uploads/1765547999709_7ae3eb70-083a-4489-9ad1-68919b23c695.jpg" },
          { id: 5750, url: "https://maximgun.ru/uploads/1765893975671_6ad2f9d4-00af-40cd-8469-f6d9f304fbee.jpg" },
          { id: 5751, url: "https://maximgun.ru/uploads/1765893976600_b5be9014-6218-4aea-8dbc-3e73933594a2.jpg" },
          { id: 5754, url: "https://maximgun.ru/uploads/1765894171268_1ee8f6d2-0e17-48a9-b4fc-f78ce3cca792.jpg" },
          { id: 5755, url: "https://maximgun.ru/uploads/1765894172082_7b254444-4b5b-4a9e-ae74-a526dc26fb46.jpg" },
          { id: 5756, url: "https://maximgun.ru/uploads/1765894172826_be60e062-d0c7-474e-9a6f-12332227d2d4.jpg" },
          { id: 5757, url: "https://maximgun.ru/uploads/1765894173837_3129fbdc-2738-44c6-a4fa-a0697163d875.jpg" },
        ], // https://maximgun.ru/details/281
      },
      3681: { featured: null, photos: [] },
      4330: {
        featured: null,
        photos: [
          { id: 5207, url: "https://maximgun.ru/uploads/1758200175554_9be98c44-b1fa-4af2-892f-e74ba5500b2e.jpg" },
          { id: 5206, url: "https://maximgun.ru/uploads/1758200172484_927fcf00-c3a2-4b03-a6b4-ddd8d7eb2531.jpg" },
          { id: 5203, url: "https://maximgun.ru/uploads/1758199506467_16c53b88-8c45-4dc0-8b38-a078d0f00367.jpg" },
        ], // https://maximgun.ru/details/217
      },
      8037: { featured: null, photos: [] },
      9326: {
        featured: null,
        photos: [
          { id: 381, url: "https://maximgun.ru/uploads/1754095132848_419f7200-b575-4853-8237-98ab87db827a.jpg" },
          { id: 382, url: "https://maximgun.ru/uploads/1754095133360_6870eb56-b06d-4f37-aa95-820707d77b20.jpg" },
          { id: 383, url: "https://maximgun.ru/uploads/1754095133832_c802f87d-b4a3-4172-b0fd-fd7de38efaa3.jpg" },
        ], // https://maximgun.ru/details/99
      },
      10679: {
        featured: 3233,
        photos: [
          { id: 3233, url: "https://maximgun.ru/uploads/1755191671881_ffefd657-8021-4afc-83f7-14d3ff25e4fb.JPG" },
        ], // https://maximgun.ru/details/135
      },
      16253: { featured: null, photos: [] },
    },
    trunk: {
      4419: {
        featured: null,
        photos: [
          { id: 5208, url: "https://maximgun.ru/uploads/1758207309221_213acfe0-9c64-42c6-a9b7-2907493040f1.JPG" },
          { id: 5209, url: "https://maximgun.ru/uploads/1758207313190_71a3af83-0436-4be2-ab42-e218aa6d2ae8.JPG" },
        ], // https://maximgun.ru/details/352
      },
      5691: { featured: null, photos: [] },
      6207: { featured: null, photos: [] },
      6799: { featured: null, photos: [] },
      8037: { featured: null, photos: [] },
      9122: { featured: null, photos: [] },
      10679: { featured: null, photos: [] },
    },
    trunkLock: {
      5691: { featured: null, photos: [] },
      6207: {
        featured: null,
        photos: [
          { id: 6267, url: "https://maximgun.ru/uploads/1768384494396_b42fb9a8-d54f-4917-b057-1162afd58892.webp" },
          { id: 6268, url: "https://maximgun.ru/uploads/1768384495209_331a8eb4-307a-41e9-8ca4-1b7bce39895e.webp" },
        ], // https://maximgun.ru/details/339
      },
      6799: {
        featured: null,
        photos: [
          { id: 8183, url: "https://maximgun.ru/uploads/1785514222526_af57cac2-a946-40c2-b057-cc7ac5be1cc4.webp" },
          { id: 8181, url: "https://maximgun.ru/uploads/1785514221059_b7379f43-aaf9-4c5e-9155-133de4f8241c.webp" },
          { id: 8185, url: "https://maximgun.ru/uploads/1785514407787_5497637d-29a8-4c60-b020-9188437d861b.webp" },
          { id: 8184, url: "https://maximgun.ru/uploads/1785514223199_3f739fcd-71d1-41f4-ae67-725cf72dded3.webp" },
          { id: 8186, url: "https://maximgun.ru/uploads/1785514408698_14268696-54b7-4356-8b8a-de7d06c6fb67.webp" },
          { id: 8187, url: "https://maximgun.ru/uploads/1785514409313_b0eb2bb0-2efb-4309-857f-48a5e6f11c1b.webp" },
          { id: 8188, url: "https://maximgun.ru/uploads/1785514409806_5a2b2ac3-19bd-434a-99d8-c0c21639257e.webp" },
          { id: 8182, url: "https://maximgun.ru/uploads/1785514221571_938787a2-cfe5-4ef7-a28b-d5776cf043f5.webp" },
          { id: 8189, url: "https://maximgun.ru/uploads/1785514410382_325105c2-a535-43a7-bc8d-00083eaa8911.webp" },
          { id: 8193, url: "https://maximgun.ru/uploads/1785514413802_59d52b3f-0f7c-43d6-970a-2fc8ba966cfc.webp" },
          { id: 8192, url: "https://maximgun.ru/uploads/1785514412921_e2eff2a8-d112-4f6c-82b9-641333e2120c.webp" },
          { id: 8195, url: "https://maximgun.ru/uploads/1785514415173_b9cf81a7-d5c5-4b59-90e7-80fd648f563f.webp" },
          { id: 8180, url: "https://maximgun.ru/uploads/1785514220113_f0d76708-3692-4ad3-bb0c-de413e5bb8a9.webp" },
          { id: 8190, url: "https://maximgun.ru/uploads/1785514411240_380c702e-7d82-45a5-8bdc-840caa4992ee.webp" },
          { id: 8191, url: "https://maximgun.ru/uploads/1785514412097_4a7a700b-ae96-4115-975c-dc8e343bfe37.webp" },
          { id: 8194, url: "https://maximgun.ru/uploads/1785514414436_b303f572-789f-4856-9f18-8d0dcd897307.webp" },
        ], // https://maximgun.ru/details/558
      },
      8037: { featured: null, photos: [] },
      9122: { featured: null, photos: [] },
    },
    axle: {
      4084: {
        featured: null,
        photos: [
          { id: 5445, url: "https://maximgun.ru/uploads/1762364794897_e7696675-b19c-4ffb-80d9-da47ffec6f55.jpg" },
          { id: 5446, url: "https://maximgun.ru/uploads/1762364795828_4a5fafdf-3b1d-4193-8db9-4c8b51ea0fea.jpg" },
          { id: 5447, url: "https://maximgun.ru/uploads/1762364796432_0e985d77-56d9-4138-9897-cc7c252b3f42.jpg" },
          { id: 5448, url: "https://maximgun.ru/uploads/1762364797081_47a1fbc6-9a4c-45d2-9be1-e935d9200d88.jpg" },
          { id: 5449, url: "https://maximgun.ru/uploads/1762364797797_d342843c-237f-4cd0-84fa-503d49361a76.jpg" },
        ], // https://maximgun.ru/details/240
      },
      5691: { featured: null, photos: [] },
      9122: { featured: null, photos: [] },
      10101: {
        featured: null,
        photos: [
          { id: 4797, url: "https://maximgun.ru/uploads/1756372368667_5ff8c400-518c-4a25-92a1-1e5281b85dd3.jpeg" },
          { id: 4793, url: "https://maximgun.ru/uploads/1756372366856_bcfe57bc-e480-4a36-a5d8-35edba6dc78f.jpeg" },
          { id: 4792, url: "https://maximgun.ru/uploads/1756372366420_794d81d4-adeb-4497-bec2-dcdb353c1046.jpeg" },
        ], // https://maximgun.ru/details/144
      },
      10679: { featured: null, photos: [] },
    },
    swivel: {
      2719: {
        featured: null,
        photos: [
          { id: 297, url: "https://maximgun.ru/uploads/1753904096378_1cbb7cd5-a5a6-4498-97ea-6d1d230544c0.JPG" },
          { id: 351, url: "https://maximgun.ru/uploads/1754093155536_17c7415d-041e-4312-84ca-6bc7da4450e5.jpg" },
        ], // https://maximgun.ru/details/88
      },
      13743: {
        featured: null,
        photos: [
          { id: 35, url: "https://maximgun.ru/uploads/1753219320976_cf6d4298-da2c-4b59-9183-50cd45c7228c.jpg" },
          { id: 36, url: "https://maximgun.ru/uploads/1753219321647_0b76b880-7f03-462c-941f-fb4876988768.jpg" },
          { id: 37, url: "https://maximgun.ru/uploads/1753219322007_f4858486-d33b-4cd3-8e3e-8e3a6d04339c.jpg" },
        ], // https://maximgun.ru/details/23
      },
      14996: {
        featured: null,
        photos: [
          { id: 380, url: "https://maximgun.ru/uploads/1754094635817_3776c1bf-66ea-4d6b-bc3a-a9bf457c54ea.jpeg" },
          { id: 377, url: "https://maximgun.ru/uploads/1754094634099_b3b90aa4-051c-4877-8092-08a2fd47dd9c.jpeg" },
        ], // https://maximgun.ru/details/98
      },
      15319: {
        featured: null,
        photos: [
          { id: 384, url: "https://maximgun.ru/uploads/1754095621109_18554025-e5ac-4d17-aa6b-21069e6b50be.jpg" },
        ], // https://maximgun.ru/details/100
      },
      18033: {
        featured: null,
        photos: [
          { id: 33, url: "https://maximgun.ru/uploads/1753218374794_96d36815-fb52-4380-9a24-8afd02fc281e.jpg" },
          { id: 34, url: "https://maximgun.ru/uploads/1753218375178_430dd2c4-91d0-4d66-9b65-0c047ce5b0ea.jpg" },
          { id: 6080, url: "https://maximgun.ru/uploads/1767125037632_c8a3915d-f51f-41e4-a224-02a962ff8fa5.jpg" },
        ], // https://maximgun.ru/details/22
      },
      3219: {
        featured: null,
        photos: [
          { id: 5334, url: "https://maximgun.ru/uploads/1761158432723_4eca9772-734b-4a92-9886-967c9e8c3376.jpg" },
          { id: 5333, url: "https://maximgun.ru/uploads/1761158427472_c5810436-32d5-45b5-a742-972574493046.jpg" },
          { id: 5332, url: "https://maximgun.ru/uploads/1761158422368_7ef74141-c3cd-4fa3-81fd-7f26333b1972.jpg" },
          { id: 5331, url: "https://maximgun.ru/uploads/1761158417403_1b114576-f78f-4508-a3b2-47b5afb85d8d.jpg" },
          { id: 5330, url: "https://maximgun.ru/uploads/1761158412079_885eb962-227e-4627-97d1-facf25693680.jpg" },
          { id: 5329, url: "https://maximgun.ru/uploads/1761158405979_0e168cb8-dc9e-4cfa-b871-f09d9e3d1e0b.jpg" },
          { id: 5328, url: "https://maximgun.ru/uploads/1761158403901_be19c80d-6e93-41d4-a4a8-4a992ac18279.jpg" },
          { id: 7891, url: "https://maximgun.ru/uploads/1784879395804_a22515f0-6420-4601-8770-111cc2bdf769.jpg" },
          { id: 7888, url: "https://maximgun.ru/uploads/1784879384990_eea1b0c6-ae5c-4304-b321-efc0e5507648.jpg" },
          { id: 7890, url: "https://maximgun.ru/uploads/1784879391151_11a562ad-7487-45a6-a757-0ef8f0630e87.jpg" },
          { id: 7893, url: "https://maximgun.ru/uploads/1784879403991_9040a993-3766-42b8-91a5-b9f1e07b83aa.jpg" },
          { id: 7887, url: "https://maximgun.ru/uploads/1784879378737_07228383-fae6-4803-8963-c607f894fc1e.jpg" },
          { id: 7894, url: "https://maximgun.ru/uploads/1784879406580_3a542992-47ff-4d58-990b-561e29ef5c78.jpg" },
          { id: 7892, url: "https://maximgun.ru/uploads/1784879399648_f7e2adac-935a-4d7a-ae84-97af6e174120.jpg" },
          { id: 7889, url: "https://maximgun.ru/uploads/1784879389054_71ef3b25-b67a-443b-a78f-cdce3c99f0cf.jpg" },
        ], // https://maximgun.ru/details/233
      },
      4322: {
        featured: null,
        photos: [
          { id: 5205, url: "https://maximgun.ru/uploads/1758199927641_8587048a-23e6-4953-b5b0-8fb13fc6628a.jpg" },
          { id: 5204, url: "https://maximgun.ru/uploads/1758199927168_adb884cf-89ea-42f4-bec3-6a4a91c68efc.jpg" },
          { id: 5356, url: "https://maximgun.ru/uploads/1761210288400_cb91f6ca-4613-49a5-94af-2b1a83227372.jpg" },
          { id: 5357, url: "https://maximgun.ru/uploads/1761210289624_9fd74ba7-31c6-46a5-bb21-d0456dacfe20.jpg" },
          { id: 5358, url: "https://maximgun.ru/uploads/1761210290614_f5cee6dd-c30c-40a2-b947-fbdf86c03e13.jpg" },
        ], // https://maximgun.ru/details/218
      },
      4541: { featured: null, photos: [] },
      6134: {
        featured: null,
        photos: [
          { id: 5758, url: "https://maximgun.ru/uploads/1765894323117_f72d32ce-a0e3-4fb0-bdbb-8987d64cd045.jpg" },
          { id: 5754, url: "https://maximgun.ru/uploads/1765894171268_1ee8f6d2-0e17-48a9-b4fc-f78ce3cca792.jpg" },
          { id: 5750, url: "https://maximgun.ru/uploads/1765893975671_6ad2f9d4-00af-40cd-8469-f6d9f304fbee.jpg" },
          { id: 5387, url: "https://maximgun.ru/uploads/1761212934513_9e24d673-e317-41cb-8d2b-6bab0591b668.jpg" },
          { id: 5695, url: "https://maximgun.ru/uploads/1765547999709_7ae3eb70-083a-4489-9ad1-68919b23c695.jpg" },
          { id: 5696, url: "https://maximgun.ru/uploads/1765548000337_83e17179-36cf-4004-9b00-ea37dbf482b4.jpg" },
          { id: 5697, url: "https://maximgun.ru/uploads/1765548001109_5fbd6ab2-b228-4483-b7a1-d705a2cd3caa.jpg" },
        ], // https://maximgun.ru/details/238
      },
      6612: {
        featured: 259,
        photos: [
          { id: 259, url: "https://maximgun.ru/uploads/1753804635332_3142b15d-d4f9-4db7-b27d-446755e183eb.jpg" },
        ], // https://maximgun.ru/details/80
      },
      8037: { featured: null, photos: [] },
      8367: {
        featured: 328,
        photos: [
          { id: 328, url: "https://maximgun.ru/uploads/1754038022193_c1a661fb-74e5-492a-8ac2-623430bd8905.jpeg" },
        ], // https://maximgun.ru/details/87
      },
      10679: {
        featured: null,
        photos: [
          { id: 3231, url: "https://maximgun.ru/uploads/1755191659815_58f1cdec-6b9d-46ba-815e-8e0ad5f0878a.JPG" },
          { id: 3232, url: "https://maximgun.ru/uploads/1755191671332_9db005d3-5835-4ddd-9685-5791fffcf997.JPG" },
        ], // https://maximgun.ru/details/133
      },
      11457: {
        featured: null,
        photos: [
          { id: 5385, url: "https://maximgun.ru/uploads/1761211240817_127602ed-8fbe-447b-97ff-82061fb91349.jpg" },
          { id: 5386, url: "https://maximgun.ru/uploads/1761211241433_8f50db93-19e5-4af1-8395-ddf741cbe9ab.jpg" },
        ], // https://maximgun.ru/details/237
      },
    },
    clamp: {
      297: {
        featured: null,
        photos: [
          { id: 5778, url: "https://maximgun.ru/uploads/1765897885116_2f461367-205a-46b4-8b6e-45b5529326c5.jpg" },
          { id: 5779, url: "https://maximgun.ru/uploads/1765897888925_7bd2a6b2-c730-48d8-a964-e1619ac96181.jpg" },
        ], // https://maximgun.ru/details/285
      },
      1928: {
        featured: 78,
        photos: [
          { id: 78, url: "https://maximgun.ru/uploads/1753402140887_62ecb414-2c5b-405a-b4f2-35bcda81ffe4.jpeg" },
        ], // https://maximgun.ru/details/48
      },
      3219: {
        featured: null,
        photos: [
          { id: 7924, url: "https://maximgun.ru/uploads/1784891477876_5cb34936-6f94-483e-a97d-639b072a415c.jpg" },
          { id: 7925, url: "https://maximgun.ru/uploads/1784891486864_46034289-f999-4da6-99f9-7d243b89d811.jpg" },
          { id: 7926, url: "https://maximgun.ru/uploads/1784891491712_8a0e94ac-e024-45f6-8ffc-267169cffd80.jpg" },
          { id: 7927, url: "https://maximgun.ru/uploads/1784891497304_ef460472-1c00-4ceb-a41d-45022e7bdba7.jpg" },
          { id: 7928, url: "https://maximgun.ru/uploads/1784891505705_a2d6cd59-c4c2-45af-b0a1-ec485c54de6a.jpg" },
          { id: 7929, url: "https://maximgun.ru/uploads/1784891510940_72d6ddbb-b4ba-4c7d-8d65-beebadd2dc7a.jpg" },
          { id: 7930, url: "https://maximgun.ru/uploads/1784891518515_55ee2305-7f34-4cdf-b385-494af6413bcc.jpg" },
          { id: 7931, url: "https://maximgun.ru/uploads/1784891524543_e95c65d1-391b-4133-ba5b-47fe5b431d98.jpg" },
          { id: 7932, url: "https://maximgun.ru/uploads/1784891530102_5bf7357e-5ab9-4ea1-8920-7a497c49a97b.jpg" },
        ], // https://maximgun.ru/details/508
      },
      6134: {
        featured: null,
        photos: [
          { id: 5695, url: "https://maximgun.ru/uploads/1765547999709_7ae3eb70-083a-4489-9ad1-68919b23c695.jpg" },
          { id: 5750, url: "https://maximgun.ru/uploads/1765893975671_6ad2f9d4-00af-40cd-8469-f6d9f304fbee.jpg" },
          { id: 5751, url: "https://maximgun.ru/uploads/1765893976600_b5be9014-6218-4aea-8dbc-3e73933594a2.jpg" },
          { id: 5754, url: "https://maximgun.ru/uploads/1765894171268_1ee8f6d2-0e17-48a9-b4fc-f78ce3cca792.jpg" },
          { id: 5759, url: "https://maximgun.ru/uploads/1765894448004_97b94d76-a330-490f-bf26-699ef8a5d658.jpg" },
        ], // https://maximgun.ru/details/282
      },
      8037: { featured: null, photos: [] },
      10679: { featured: null, photos: [] },
      14818: {
        featured: 3232,
        photos: [
          { id: 3232, url: "https://maximgun.ru/uploads/1755191671332_9db005d3-5835-4ddd-9685-5791fffcf997.JPG" },
        ], // https://maximgun.ru/details/134
      },
    },
    rearRuler: {
      2531: {
        featured: null,
        photos: [
          { id: 6453, url: "https://maximgun.ru/uploads/1769719362976_cc65c604-9a50-4465-9984-66672ee42c09.jpg" },
        ], // https://maximgun.ru/details/369
      },
      8037: { featured: null, photos: [] },
      9326: {
        featured: null,
        photos: [
          { id: 381, url: "https://maximgun.ru/uploads/1754095132848_419f7200-b575-4853-8237-98ab87db827a.jpg" },
          { id: 382, url: "https://maximgun.ru/uploads/1754095133360_6870eb56-b06d-4f37-aa95-820707d77b20.jpg" },
          { id: 383, url: "https://maximgun.ru/uploads/1754095133832_c802f87d-b4a3-4172-b0fd-fd7de38efaa3.jpg" },
        ], // https://maximgun.ru/details/99
      },
      16253: { featured: null, photos: [] },
    },
    coarse: {
      807: {
        featured: null,
        photos: [
          { id: 5560, url: "https://maximgun.ru/uploads/1763475280224_b484b175-c54b-4e84-a297-e59c5777ea90.jpg" },
          { id: 5561, url: "https://maximgun.ru/uploads/1763475280642_aced3790-ef1d-4be3-b174-f7dc109f91e9.jpg" },
          { id: 5562, url: "https://maximgun.ru/uploads/1763475280939_4f5f057d-629b-4902-8a08-8c07e0a9b24e.jpg" },
          { id: 5563, url: "https://maximgun.ru/uploads/1763475281274_2c399b33-16f6-4133-aa9b-763d500c534e.jpg" },
          { id: 5564, url: "https://maximgun.ru/uploads/1763475281560_556ebb05-f830-4938-b908-a9f9bb0f747d.jpg" },
          { id: 5565, url: "https://maximgun.ru/uploads/1763475281876_f85e6ceb-deb1-4e53-9603-edd5578f3da1.jpg" },
        ], // https://maximgun.ru/details/256
      },
      974: {
        featured: null,
        photos: [
          { id: 6437, url: "https://maximgun.ru/uploads/1769712570985_8b734496-b3a6-4133-8137-e1a3c16d99f0.jpg" },
          { id: 6438, url: "https://maximgun.ru/uploads/1769712572527_40921652-68a0-4048-b87a-c527a22fa576.jpg" },
          { id: 6443, url: "https://maximgun.ru/uploads/1769717733815_529f9fd5-7b0a-4c8a-a3d2-868869b74ce9.jpg" },
          { id: 6444, url: "https://maximgun.ru/uploads/1769717738193_84ba7e04-7827-429c-86b3-22b1b38d8057.jpg" },
        ], // https://maximgun.ru/details/362
      },
      3219: {
        featured: null,
        photos: [
          { id: 6452, url: "https://maximgun.ru/uploads/1769719070577_a1ac342b-1567-40c0-a35d-25af7092a265.jpg" },
          { id: 7911, url: "https://maximgun.ru/uploads/1784891146089_370c7214-68f0-4b8d-912d-046e35adb152.jpg" },
          { id: 7912, url: "https://maximgun.ru/uploads/1784891157076_fe85d0d4-17ba-44c6-aae0-bf889c68acb2.jpg" },
          { id: 7921, url: "https://maximgun.ru/uploads/1784891209452_8e9bbddc-cc67-4d02-b9bc-c994eec9b5a0.jpg" },
          { id: 7910, url: "https://maximgun.ru/uploads/1784891140690_faf8982c-8122-4bf6-9f05-4c3d07c98072.jpg" },
          { id: 7923, url: "https://maximgun.ru/uploads/1784891224502_175d6e74-245c-41d4-867f-737c845a0e08.jpg" },
          { id: 7919, url: "https://maximgun.ru/uploads/1784891197148_7722a388-53c4-4d49-9fdf-b43d348c7802.jpg" },
          { id: 7918, url: "https://maximgun.ru/uploads/1784891192105_5440420c-2051-4f34-acd7-62c95f7abc54.jpg" },
          { id: 7922, url: "https://maximgun.ru/uploads/1784891218475_b79abac6-de7c-4f32-82f7-bab0a292f067.jpg" },
          { id: 7920, url: "https://maximgun.ru/uploads/1784891203119_1380ab0a-4755-41af-a5f5-1504fdbaa14b.jpg" },
          { id: 7913, url: "https://maximgun.ru/uploads/1784891161775_6836ff8a-485c-4968-a4f9-eac5cc769fe9.jpg" },
          { id: 7914, url: "https://maximgun.ru/uploads/1784891167778_702ae5be-c873-44ab-b8fa-b7e48f8501ca.jpg" },
          { id: 7917, url: "https://maximgun.ru/uploads/1784891184835_1f42b90d-9561-4953-b9ea-4945e7166659.jpg" },
          { id: 7915, url: "https://maximgun.ru/uploads/1784891174107_8a159d47-a79c-43eb-94a9-2210179035a9.jpg" },
          { id: 7916, url: "https://maximgun.ru/uploads/1784891179593_dc02db78-a085-4333-98b7-0c541e7b2dd7.jpg" },
          { id: 7974, url: "https://maximgun.ru/uploads/1784896358705_014814a1-bd2a-41b0-a337-baff86bc3515.jpg" },
        ], // https://maximgun.ru/details/368
      },
      3497: {
        featured: null,
        photos: [
          { id: 6451, url: "https://maximgun.ru/uploads/1769718918909_7eb92028-a2b9-4560-893c-38902744bb39.jpg" },
        ], // https://maximgun.ru/details/367
      },
      3979: {
        featured: null,
        photos: [
          { id: 6448, url: "https://maximgun.ru/uploads/1769718626257_776a9d80-8793-4e3e-a1c6-8f87f4cdbaeb.jpg" },
        ], // https://maximgun.ru/details/364
      },
      4391: {
        featured: null,
        photos: [
          { id: 7074, url: "https://maximgun.ru/uploads/1775576578912_de40db9a-192d-4397-89ba-67e5a5887175.jpg" },
          { id: 7075, url: "https://maximgun.ru/uploads/1775576579554_3b1e2440-ae02-4ee5-8bf8-5c813f0a06fa.jpg" },
          { id: 7076, url: "https://maximgun.ru/uploads/1775576580424_196afc9b-07a8-4c8c-9e1a-8e2deb7b5dfd.jpg" },
          { id: 7077, url: "https://maximgun.ru/uploads/1775576581462_6c466003-8b5a-4ce1-bdf8-05964c398762.jpg" },
        ], // https://maximgun.ru/details/467
      },
      5349: {
        featured: null,
        photos: [
          { id: 6450, url: "https://maximgun.ru/uploads/1769718843988_4053f3cc-57db-4d5b-9f81-8718388dc94f.jpg" },
        ], // https://maximgun.ru/details/366
      },
      6014: {
        featured: null,
        photos: [
          { id: 6449, url: "https://maximgun.ru/uploads/1769718731570_4073850e-18a6-4e54-87f2-3fa8b8f49800.jpg" },
          { id: 7312, url: "https://maximgun.ru/uploads/1780312780439_ead9baaf-a6f3-41d3-bb8e-bcb397523c54.jpg" },
        ], // https://maximgun.ru/details/365
      },
      6612: { featured: null, photos: [] },
      8037: { featured: null, photos: [] },
      8367: { featured: null, photos: [] },
      9122: { featured: null, photos: [] },
      10679: { featured: null, photos: [] },
    },
    fine: {
      1968: {
        featured: null,
        photos: [
          { id: 7961, url: "https://maximgun.ru/uploads/1784895927213_d0a6f739-cafd-4196-bc02-e6b4e6467603.jpg" },
          { id: 7962, url: "https://maximgun.ru/uploads/1784895932604_197a1f5b-3a44-4886-9d8d-26c594936393.jpg" },
          { id: 7963, url: "https://maximgun.ru/uploads/1784895938491_ec7deb4f-0cfc-4d9c-a40a-cdc2d3386aaf.jpg" },
          { id: 7964, url: "https://maximgun.ru/uploads/1784895944644_473ca4e1-de1a-4eb9-b23b-2a10bb334fc9.jpg" },
          { id: 7965, url: "https://maximgun.ru/uploads/1784895949652_c1d17d45-8129-4d3c-887b-350d602f8fac.jpg" },
          { id: 7966, url: "https://maximgun.ru/uploads/1784895955009_785227a4-bf5f-4332-bd8d-6019316fa988.jpg" },
          { id: 7967, url: "https://maximgun.ru/uploads/1784895959945_1ab28a6a-2f85-4584-9bf6-c8cc06b0dc5c.jpg" },
          { id: 7972, url: "https://maximgun.ru/uploads/1784895991883_5dc22eaa-7e48-496f-887c-e52f828feb57.jpg" },
          { id: 7968, url: "https://maximgun.ru/uploads/1784895966881_6b3ddb49-fbf9-4de3-9f05-5dc7da18815c.jpg" },
          { id: 7969, url: "https://maximgun.ru/uploads/1784895972892_fa26e889-208c-4542-978c-93051021b9f9.jpg" },
          { id: 7970, url: "https://maximgun.ru/uploads/1784895979616_3cdd7c1f-490c-4127-94e1-0bbd1c93b5f7.jpg" },
          { id: 7973, url: "https://maximgun.ru/uploads/1784895997820_223666c2-b94a-424b-8ce4-a40bd87fc3d7.jpg" },
          { id: 7971, url: "https://maximgun.ru/uploads/1784895986101_8ab8d44a-a51e-438e-9416-498a0af042a5.jpg" },
          { id: 8039, url: "https://maximgun.ru/uploads/1785055799112_df20a6b8-d14f-44e3-b356-ecb09d20bb93.jpg" },
        ], // https://maximgun.ru/details/511
      },
      6817: {
        featured: null,
        photos: [
          { id: 7935, url: "https://maximgun.ru/uploads/1784892153736_cc939eb4-99ad-493b-bcd9-8ca364fbb468.jpg" },
          { id: 7933, url: "https://maximgun.ru/uploads/1784892136809_1b302c7c-9750-4553-ae91-ad7d78620bbc.jpg" },
          { id: 7934, url: "https://maximgun.ru/uploads/1784892147613_afb8616f-6d77-4e7e-851e-be6fcf10c791.jpg" },
          { id: 7936, url: "https://maximgun.ru/uploads/1784892160836_08759635-2bd1-43f0-9f39-134e5c902f3a.jpg" },
          { id: 7937, url: "https://maximgun.ru/uploads/1784892168989_aa7544e4-9459-4f42-afab-6ef0d8e7d660.jpg" },
          { id: 7938, url: "https://maximgun.ru/uploads/1784892175976_cecf9529-0ac7-4eef-a389-56566cdbfccc.jpg" },
          { id: 7939, url: "https://maximgun.ru/uploads/1784892182138_a4bac4b9-0df4-44c3-8a77-4e04d7962840.jpg" },
          { id: 7940, url: "https://maximgun.ru/uploads/1784892188833_429cf0b8-15e2-4108-b1f0-ac43845d59e9.jpg" },
          { id: 7941, url: "https://maximgun.ru/uploads/1784892196909_893ada35-df8b-43fb-8f9d-45f6d6909fbe.jpg" },
          { id: 7942, url: "https://maximgun.ru/uploads/1784892202913_abec0fc3-9ebe-411e-b5e9-1ba55188e257.jpg" },
          { id: 7943, url: "https://maximgun.ru/uploads/1784892209184_1a243f28-e392-4973-bbaf-c3fec20e2fd5.jpg" },
        ], // https://maximgun.ru/details/509
      },
      9581: {
        featured: null,
        photos: [
          { id: 8045, url: "https://maximgun.ru/uploads/1785060421504_a9c188e9-91cd-4d75-a286-02140384e4a2.jpg" },
          { id: 8046, url: "https://maximgun.ru/uploads/1785060425880_f9827a0b-b205-4643-a1ea-a4504667ea7a.jpg" },
          { id: 8047, url: "https://maximgun.ru/uploads/1785060431232_966e218c-36f9-47d0-9a32-94d32121bf1d.jpg" },
          { id: 8048, url: "https://maximgun.ru/uploads/1785060437389_3f8a3eb0-620d-44e2-a116-8f9e8a3daea9.jpg" },
          { id: 8049, url: "https://maximgun.ru/uploads/1785060442316_8b5031e7-4a65-4346-adb9-378a1d413c77.jpg" },
          { id: 8050, url: "https://maximgun.ru/uploads/1785060446962_baa71d8b-643d-4f3d-b67b-1f8765cb48d7.jpg" },
          { id: 8051, url: "https://maximgun.ru/uploads/1785060452118_afbffd27-2bf2-4cd5-9e96-7ed7c5879b29.jpg" },
        ], // https://maximgun.ru/details/537
      },
      13768: {
        featured: null,
        photos: [
          { id: 5193, url: "https://maximgun.ru/uploads/1757978519787_3a5b363e-2baf-4989-9316-b7c175e0e0c3.webp" },
        ], // https://maximgun.ru/details/534
      },
    },
    level: {
      2490: {
        featured: null,
        photos: [
          { id: 5659, url: "https://maximgun.ru/uploads/1764773392924_0988220b-df36-42c5-af2d-18b418e0e092.png" },
          { id: 5658, url: "https://maximgun.ru/uploads/1764773392116_e3c71853-ca05-4fa6-89d4-4831a8e17bed.png" },
        ], // https://maximgun.ru/details/274
      },
    },
    link: {
      635: {
        featured: null,
        photos: [
          { id: 202, url: "https://maximgun.ru/uploads/1753686643019_2f5efd49-9ee2-4f62-825b-bb2624d247bc.jpeg" },
          { id: 201, url: "https://maximgun.ru/uploads/1753686642622_c891c06c-50e1-4e7f-8c80-2257a305ca22.jpeg" },
          { id: 203, url: "https://maximgun.ru/uploads/1753686643368_1d537752-0d4e-4c82-97c6-c02329336817.jpeg" },
        ], // https://maximgun.ru/details/78
      },
      792: {
        featured: null,
        photos: [
          { id: 223, url: "https://maximgun.ru/uploads/1753689441820_96f3171f-bc72-4f8b-b364-0daf009fd858.jpg" },
          { id: 224, url: "https://maximgun.ru/uploads/1753689442116_fadd2a71-f94a-453a-b9d8-f8014080b22c.jpg" },
          { id: 225, url: "https://maximgun.ru/uploads/1753689442418_78312ae1-4355-44dc-93ab-260ccf4ef477.jpg" },
        ], // https://maximgun.ru/details/77
      },
      6241: {
        featured: null,
        photos: [
          { id: 5444, url: "https://maximgun.ru/uploads/1762333110311_a2595e3c-a98f-4b47-9ab2-9c2a6d94dfcb.jpg" },
          { id: 5443, url: "https://maximgun.ru/uploads/1762333109753_6aadcc6b-d98e-45a5-a6ba-27b4aeb503ff.jpg" },
          { id: 373, url: "https://maximgun.ru/uploads/1754094298594_e2b5b05d-5659-40f8-9d4f-9f153bdc6ab0.jpg" },
          { id: 372, url: "https://maximgun.ru/uploads/1754094298107_4dedf8f4-7803-447a-858a-13787d956beb.jpg" },
          { id: 371, url: "https://maximgun.ru/uploads/1754094297435_4a646790-dbca-4eaa-91d7-a4cca1444fa4.jpg" },
          { id: 370, url: "https://maximgun.ru/uploads/1754094296680_a6813f63-d38a-44be-98a2-dc05686662e0.jpg" },
          { id: 369, url: "https://maximgun.ru/uploads/1754094295941_5d1f8cad-0ee8-47fb-b697-6bb4b68a35cb.jpg" },
          { id: 368, url: "https://maximgun.ru/uploads/1754094295423_d191c1a4-fd05-45c2-bc15-84666d0864b6.jpg" },
        ], // https://maximgun.ru/details/94
      },
      6799: {
        featured: null,
        photos: [
          { id: 374, url: "https://maximgun.ru/uploads/1754094376502_b95835ab-c6d2-4d45-aaa7-14ae3ecb252f.png" },
          { id: 8192, url: "https://maximgun.ru/uploads/1785514412921_e2eff2a8-d112-4f6c-82b9-641333e2120c.webp" },
          { id: 8190, url: "https://maximgun.ru/uploads/1785514411240_380c702e-7d82-45a5-8bdc-840caa4992ee.webp" },
        ], // https://maximgun.ru/details/95
      },
      10137: {
        featured: null,
        photos: [
          { id: 357, url: "https://maximgun.ru/uploads/1754093933643_42a7dd61-9f9b-4312-b747-0851d020b754.jpg" },
          { id: 358, url: "https://maximgun.ru/uploads/1754093934017_16225ac7-2601-4159-8559-301c2ca03942.jpg" },
          { id: 359, url: "https://maximgun.ru/uploads/1754093934400_9e99426c-add1-42bc-b349-1a0bfce8fdb1.jpg" },
          { id: 360, url: "https://maximgun.ru/uploads/1754093934686_7ebdc6ed-eb17-4292-8f98-aea61acce185.jpg" },
          { id: 361, url: "https://maximgun.ru/uploads/1754093934994_0dcc2cae-8c04-44ef-9b13-53dd311c174e.jpg" },
          { id: 362, url: "https://maximgun.ru/uploads/1754093935380_b091bad4-1bb2-4f8a-a06c-b53efe73e5a2.jpg" },
        ], // https://maximgun.ru/details/91
      },
      10679: {
        featured: null,
        photos: [
          { id: 379, url: "https://maximgun.ru/uploads/1754094635231_a77f8271-2c99-4dc2-90e9-50118ce00f71.jpeg" },
          { id: 378, url: "https://maximgun.ru/uploads/1754094634705_9e8d8ac1-b6f5-4791-950a-44e129ad7445.jpeg" },
        ], // https://maximgun.ru/details/97
      },
      12213: {
        featured: null,
        photos: [
          { id: 407, url: "https://maximgun.ru/uploads/1754249899652_8df77503-9f32-4272-b9e7-e2d04acbe541.jpg" },
          { id: 3230, url: "https://maximgun.ru/uploads/1755177210193_39e01ae2-d3aa-48ce-bfd5-26c5d157c1c7.jpg" },
        ], // https://maximgun.ru/details/117
      },
      3060: {
        featured: null,
        photos: [
          { id: 5347, url: "https://maximgun.ru/uploads/1761204252872_9a67949c-350a-4538-a845-9a2992060e2f.jpg" },
          { id: 5346, url: "https://maximgun.ru/uploads/1761204248787_ef1c9180-2f5c-4efa-b0fb-ccb8added250.jpg" },
        ], // https://maximgun.ru/details/235
      },
      5691: { featured: null, photos: [] },
      6200: {
        featured: null,
        photos: [
          { id: 365, url: "https://maximgun.ru/uploads/1754094171593_d764a9c2-7dcf-4edc-881e-c7f89ef342f9.jpg" },
          { id: 367, url: "https://maximgun.ru/uploads/1754094172414_9ca22630-7981-49bd-b215-2e073d3473f7.jpg" },
          { id: 5442, url: "https://maximgun.ru/uploads/1762332779511_799f8152-bb15-4eae-9611-854f6f39ee4c.jpg" },
          { id: 366, url: "https://maximgun.ru/uploads/1754094171992_789d28cc-2440-4bdc-9cf3-c0d46951d5a9.jpg" },
          { id: 364, url: "https://maximgun.ru/uploads/1754094171247_2db7f079-b473-4179-9214-0e715e5099dc.jpg" },
          { id: 363, url: "https://maximgun.ru/uploads/1754094170838_75017a9b-272d-477d-85a7-59c1126043ef.jpg" },
          { id: 5438, url: "https://maximgun.ru/uploads/1762332778379_6ea1ea1d-9799-42f9-a2df-a9e7e8f1c495.jpg" },
          { id: 5439, url: "https://maximgun.ru/uploads/1762332778638_7e7f66d3-d2dc-408f-84fd-fb093e214ebd.jpg" },
          { id: 5440, url: "https://maximgun.ru/uploads/1762332778990_b5d35961-ebb3-4131-a3f0-4be458cbcec1.jpg" },
          { id: 5441, url: "https://maximgun.ru/uploads/1762332779265_1ecb5360-5614-4b89-b6ab-4d37e7ba2560.jpg" },
        ], // https://maximgun.ru/details/93
      },
    },
    seat: {
      4419: { featured: null, photos: [] },
      4541: { featured: null, photos: [] },
      5691: { featured: null, photos: [] },
    },
  },
  ORZ: {
    skis: {
      201: { featured: null, photos: [] },
      2599: {
        featured: 6241,
        photos: [
          { id: 6241, url: "https://maximgun.ru/uploads/1768378976924_4875f420-cb22-4cf9-a3bd-2984d732ba50.webp" },
        ], // https://maximgun.ru/details/545
      },
      2715: {
        featured: 21,
        photos: [
          { id: 21, url: "https://maximgun.ru/uploads/1752764011415_bbea5dab-d2d2-423e-a63d-e6c98f6d4f83.jpg" },
        ], // https://maximgun.ru/details/13
      },
      4681: { featured: null, photos: [] },
      5429: {
        featured: null,
        photos: [
          { id: 3201, url: "https://maximgun.ru/uploads/1755017897081_5e610713-aac1-4572-8dd0-49bbc1929c45.jpeg" },
          { id: 3200, url: "https://maximgun.ru/uploads/1755017896391_92d10ef5-8c3e-4ef3-824e-000f236b4d9e.jpeg" },
          { id: 3199, url: "https://maximgun.ru/uploads/1755017895756_3eb8fcbb-a3fb-43ef-9797-449c7cd5a88b.jpeg" },
          { id: 3196, url: "https://maximgun.ru/uploads/1755017777659_0e476054-493a-4132-9361-fd756305c967.jpeg" },
          { id: 3195, url: "https://maximgun.ru/uploads/1755017776675_e8939f67-70bc-46dd-a48b-94f99453e834.jpeg" },
        ], // https://maximgun.ru/details/130
      },
      6385: {
        featured: null,
        photos: [
          { id: 5498, url: "https://maximgun.ru/uploads/1763115413202_3bfd6ea6-b734-48fd-9a60-7a74f6658aaf.jpg" },
          { id: 5497, url: "https://maximgun.ru/uploads/1763115412827_681e5a19-40d1-4341-b4ca-01e90219b2cc.jpg" },
          { id: 5496, url: "https://maximgun.ru/uploads/1763115412478_5e2ceeac-01c1-4748-8fa2-2e942832e289.jpg" },
          { id: 5495, url: "https://maximgun.ru/uploads/1763115412201_7e5bb428-c2ad-4d90-aeaa-7aea15db16e0.jpg" },
          { id: 5494, url: "https://maximgun.ru/uploads/1763115411939_7b4a0218-7fe6-4ceb-86d1-e3015e8940c0.jpg" },
          { id: 5493, url: "https://maximgun.ru/uploads/1763115411632_c9eba6bf-f819-408a-8c75-8d425c0b989e.jpg" },
        ], // https://maximgun.ru/details/250
      },
    },
    legs: {
      2599: {
        featured: 6241,
        photos: [
          { id: 6241, url: "https://maximgun.ru/uploads/1768378976924_4875f420-cb22-4cf9-a3bd-2984d732ba50.webp" },
        ], // https://maximgun.ru/details/545
      },
      4681: { featured: null, photos: [] },
      5551: { featured: null, photos: [] },
    },
    table: {
      726: {
        featured: 5489,
        photos: [
          { id: 5489, url: "https://maximgun.ru/uploads/1763065163628_3067bac2-0f69-4c2d-b603-0ff2d3d92281.jpg" },
        ], // https://maximgun.ru/details/248
      },
      2758: {
        featured: null,
        photos: [
          { id: 3179, url: "https://maximgun.ru/uploads/1755017489215_3a408498-9765-4e8e-95c7-5a9c6c68eb2c.jpeg" },
          { id: 3178, url: "https://maximgun.ru/uploads/1755017488617_7e867cb2-9aa4-4040-a3d3-862821e63f7b.jpeg" },
          { id: 3177, url: "https://maximgun.ru/uploads/1755017488125_f987ad5d-c7fc-455e-9ad0-66a9d4c64d38.jpeg" },
          { id: 3176, url: "https://maximgun.ru/uploads/1755017487591_42e20e9a-e2fc-4742-a196-9e455ead47df.jpeg" },
          { id: 3175, url: "https://maximgun.ru/uploads/1755017487111_90e90f5a-bfcd-4040-915b-c10f439dffcc.jpeg" },
          { id: 3174, url: "https://maximgun.ru/uploads/1755017486541_361da5df-b06d-42ff-aed7-bac012bbb5f7.jpeg" },
        ], // https://maximgun.ru/details/125
      },
      3270: {
        featured: null,
        photos: [
          { id: 29, url: "https://maximgun.ru/uploads/1752764920971_549aa099-6d34-4d8a-aced-f80623b56954.jpg" },
          { id: 3155, url: "https://maximgun.ru/uploads/1754857920166_eee6633c-d9d6-4303-a1d7-36b6eec16da7.jpg" },
          { id: 3156, url: "https://maximgun.ru/uploads/1754857921405_b0b8c1d6-a844-4ee9-bb04-fe5f792b912c.jpg" },
        ], // https://maximgun.ru/details/18
      },
      4173: {
        featured: null,
        photos: [
          { id: 3560, url: "https://maximgun.ru/uploads/1755886458585_1595ed1a-1f12-4511-a722-e90ae1549216.jpg" },
          { id: 3561, url: "https://maximgun.ru/uploads/1755886458917_a87ba431-bdd8-4d63-9a9f-9d44b86de223.jpg" },
        ], // https://maximgun.ru/details/141
      },
      4681: { featured: null, photos: [] },
      5337: {
        featured: null,
        photos: [
          { id: 6390, url: "https://maximgun.ru/uploads/1769686934878_54c9b31d-fa2b-44e8-a1cc-c5503ea13dd0.jpg" },
          { id: 6391, url: "https://maximgun.ru/uploads/1769686935507_195e17c6-4c6e-4246-a9f6-33dc17b9d546.jpg" },
          { id: 6392, url: "https://maximgun.ru/uploads/1769686935924_aace6d8a-7e98-4ba9-975a-2d495cc6b53b.jpg" },
          { id: 6393, url: "https://maximgun.ru/uploads/1769686936639_48bad71d-eed6-415f-b7aa-d83a2175df71.jpg" },
        ], // https://maximgun.ru/details/356
      },
      5551: {
        featured: null,
        photos: [
          { id: 8084, url: "https://maximgun.ru/uploads/1785244314857_3751b03c-91bf-4124-9549-45373732237c.jpg" },
          { id: 6933, url: "https://maximgun.ru/uploads/1772445772042_146db6b9-6cbb-4871-8d22-64c7c6c8323f.JPG" },
          { id: 8085, url: "https://maximgun.ru/uploads/1785244317494_e76b9d38-6391-40d5-9440-f3232bef632b.jpg" },
        ], // https://maximgun.ru/details/542
      },
    },
    trunk: {
      275: {
        featured: null,
        photos: [
          { id: 3462, url: "https://maximgun.ru/uploads/1755680795526_388e0354-2316-4010-861e-265622f7c182.jpg" },
          { id: 3463, url: "https://maximgun.ru/uploads/1755680796223_2e1d90bb-933c-4bec-9f12-fc2a351119f9.jpg" },
          { id: 3464, url: "https://maximgun.ru/uploads/1755680796846_a31b6fc1-4427-430a-a51d-e93172342843.jpg" },
        ], // https://maximgun.ru/details/136
      },
      2061: {
        featured: null,
        photos: [
          { id: 5749, url: "https://maximgun.ru/uploads/1765893898132_8c20b9a1-2d44-44f9-8198-b095aa3b0693.jpg" },
          { id: 5750, url: "https://maximgun.ru/uploads/1765893975671_6ad2f9d4-00af-40cd-8469-f6d9f304fbee.jpg" },
          { id: 5751, url: "https://maximgun.ru/uploads/1765893976600_b5be9014-6218-4aea-8dbc-3e73933594a2.jpg" },
          { id: 5344, url: "https://maximgun.ru/uploads/1761159154555_f057e255-1f14-48d4-8d07-ed5a3cbeef8f.jpg" },
          { id: 5345, url: "https://maximgun.ru/uploads/1761159161306_75a4df8d-5142-4a49-a306-edd0a1205366.jpg" },
        ], // https://maximgun.ru/details/234
      },
      4681: {
        featured: null,
        photos: [
          { id: 5454, url: "https://maximgun.ru/uploads/1762546179211_2bfda064-98d1-453d-ba1a-2fd2f5f664cf.jpg" },
          { id: 5455, url: "https://maximgun.ru/uploads/1762546182578_5bb8fb64-3d37-472f-9e33-6384d485e6f6.jpg" },
          { id: 5456, url: "https://maximgun.ru/uploads/1762546186461_6e96ad9d-a5e1-4706-8a9d-152f9ae6c3c6.jpg" },
        ], // https://maximgun.ru/details/242
      },
      5429: {
        featured: null,
        photos: [
          { id: 3196, url: "https://maximgun.ru/uploads/1755017777659_0e476054-493a-4132-9361-fd756305c967.jpeg" },
          { id: 3195, url: "https://maximgun.ru/uploads/1755017776675_e8939f67-70bc-46dd-a48b-94f99453e834.jpeg" },
          { id: 3194, url: "https://maximgun.ru/uploads/1755017776173_e477bce4-5413-4ce8-acd8-825275ad0889.jpeg" },
          { id: 3193, url: "https://maximgun.ru/uploads/1755017775469_c90cd32b-4742-4f1a-a64c-7991879ec27e.jpeg" },
        ], // https://maximgun.ru/details/128
      },
    },
    trunkLock: {
      2599: {
        featured: 6234,
        photos: [
          { id: 6234, url: "https://maximgun.ru/uploads/1768378458628_d436c0e1-a186-4a38-9708-5b844c005099.webp" },
        ], // https://maximgun.ru/details/335
      },
      4681: { featured: null, photos: [] },
      5429: { featured: null, photos: [] },
      5551: { featured: null, photos: [] },
    },
    axle: {
      5429: {
        featured: null,
        photos: [
          { id: 3198, url: "https://maximgun.ru/uploads/1755017824169_307d5691-442c-4228-bbe5-616dc8edc491.jpeg" },
          { id: 3197, url: "https://maximgun.ru/uploads/1755017823214_3747ab08-9ae2-46a9-909b-f79fb08224ae.jpeg" },
        ], // https://maximgun.ru/details/129
      },
    },
    swivel: {
      2557: {
        featured: 6982,
        photos: [
          { id: 6982, url: "https://maximgun.ru/uploads/1772548906473_25480f62-509d-49bb-87bc-2f9c6885c671.jpg" },
        ], // https://maximgun.ru/details/448
      },
      2758: {
        featured: null,
        photos: [
          { id: 3168, url: "https://maximgun.ru/uploads/1755017367942_3b58b4ca-9bd5-4b3e-aac5-1d9decccd723.jpeg" },
          { id: 3202, url: "https://maximgun.ru/uploads/1755018084690_24c65c3d-377b-4171-b3b1-348db5bf5cf1.jpeg" },
          { id: 3164, url: "https://maximgun.ru/uploads/1755017364839_bd6fa46d-211a-4c31-b709-ae5c34fd2ba4.jpeg" },
          { id: 3165, url: "https://maximgun.ru/uploads/1755017365592_5490e04e-5db4-4a06-b98f-f8c8c02fb569.jpeg" },
          { id: 3162, url: "https://maximgun.ru/uploads/1755017363602_7a09ddc5-526c-48b5-bf06-ba604c7c3618.jpeg" },
          { id: 3161, url: "https://maximgun.ru/uploads/1755017362996_a6a63025-774e-43c3-8601-a047626e20ae.jpeg" },
          { id: 3173, url: "https://maximgun.ru/uploads/1755017371022_64f3e292-42c3-4cc6-97ec-16135c0bb04a.jpeg" },
          { id: 3153, url: "https://maximgun.ru/uploads/1754850685210_e13d604f-f19a-4f14-8ec4-1629fa3b284d.jpeg" },
          { id: 3152, url: "https://maximgun.ru/uploads/1754850684412_e1c87ca9-aa04-4189-9931-4cdc71a63d3b.jpeg" },
          { id: 3163, url: "https://maximgun.ru/uploads/1755017364207_33dd19de-ccc2-4161-ba5d-6c6871b17e91.jpeg" },
          { id: 3172, url: "https://maximgun.ru/uploads/1755017370445_3a30b607-8a76-48fc-bd87-80ad8f833fa2.jpeg" },
          { id: 3151, url: "https://maximgun.ru/uploads/1754850683648_f36d5c44-e5d8-46ed-ac70-1f13a8a785b1.jpeg" },
          { id: 3167, url: "https://maximgun.ru/uploads/1755017367150_3e049002-efc8-4784-b2e4-63189679bc81.jpeg" },
          { id: 3150, url: "https://maximgun.ru/uploads/1754850682737_cd3a62c0-998f-4474-a71c-94db4417f9b6.jpeg" },
          { id: 3154, url: "https://maximgun.ru/uploads/1754850686032_f8e23612-e38a-4cdf-aaf6-70a57a44decd.jpeg" },
          { id: 3171, url: "https://maximgun.ru/uploads/1755017369864_249ccfa6-40a4-47a6-a790-47c48dd7f512.jpeg" },
          { id: 3170, url: "https://maximgun.ru/uploads/1755017369251_2c4ec2a7-8e69-4abb-83da-99e9668b4a16.jpeg" },
          { id: 3166, url: "https://maximgun.ru/uploads/1755017366207_4d355bc9-5e90-4bd3-b328-884dd73765b1.jpeg" },
          { id: 3169, url: "https://maximgun.ru/uploads/1755017368631_25ea5899-083f-432f-bd79-a3d649ef31aa.jpeg" },
        ], // https://maximgun.ru/details/123
      },
      3270: {
        featured: null,
        photos: [
          { id: 3157, url: "https://maximgun.ru/uploads/1754857989422_813150b6-3e8b-4e9a-bc05-3c8e7cc6d176.jpg" },
          { id: 3158, url: "https://maximgun.ru/uploads/1754857989823_574552df-e059-461a-b2eb-5d0ec16ed0d4.jpg" },
          { id: 3159, url: "https://maximgun.ru/uploads/1754857990255_d8761718-cd2d-4485-a6ad-49bb7e56aef9.jpg" },
          { id: 3160, url: "https://maximgun.ru/uploads/1754857990706_728f0857-1078-4614-a233-4d33ba25bd2c.jpg" },
          { id: 3156, url: "https://maximgun.ru/uploads/1754857921405_b0b8c1d6-a844-4ee9-bb04-fe5f792b912c.jpg" },
        ], // https://maximgun.ru/details/124
      },
      4615: {
        featured: null,
        photos: [
          { id: 325, url: "https://maximgun.ru/uploads/1753978037387_3570b7ca-b1a2-4cc4-a02b-bbc8cfad4030.jpg" },
          { id: 326, url: "https://maximgun.ru/uploads/1753978038045_3a29893d-ba41-4daf-9b6f-d4e24ead2479.jpg" },
          { id: 327, url: "https://maximgun.ru/uploads/1753978038511_79867d8e-a991-4afd-8fc5-8e2cd799de76.jpg" },
        ], // https://maximgun.ru/details/86
      },
    },
    clamp: {
      784: {
        featured: null,
        photos: [
          { id: 5484, url: "https://maximgun.ru/uploads/1762798533695_59ab8b4b-5b7c-44b4-ad12-0ea549794dc3.jpg" },
          { id: 7851, url: "https://maximgun.ru/uploads/1784232385260_ec8a2f31-eb64-4c8d-a00d-cd9e2b4d79df.jpeg" },
          { id: 7852, url: "https://maximgun.ru/uploads/1784232386375_487872e0-a451-4255-b1c5-c5541633d22a.jpeg" },
        ], // https://maximgun.ru/details/247
      },
      2758: {
        featured: null,
        photos: [
          { id: 3187, url: "https://maximgun.ru/uploads/1755017603521_0fb5a0a4-0a33-4dc8-b2f6-9279f663814e.jpeg" },
          { id: 3189, url: "https://maximgun.ru/uploads/1755017604655_3e2592cf-6088-48a0-97aa-25b9756815ea.jpeg" },
          { id: 3188, url: "https://maximgun.ru/uploads/1755017604066_9dc1edc0-4572-4db4-97ac-61b5ce5956d0.jpeg" },
          { id: 3186, url: "https://maximgun.ru/uploads/1755017603039_f512cc96-5e71-44aa-93dd-cddcd20998a0.jpeg" },
          { id: 3185, url: "https://maximgun.ru/uploads/1755017602400_f1c954b5-5fc9-4b56-a13f-8e56d47c775b.jpeg" },
          { id: 3180, url: "https://maximgun.ru/uploads/1755017599713_079289d1-2987-45c0-941c-d64b4c401f8f.jpeg" },
          { id: 3184, url: "https://maximgun.ru/uploads/1755017601867_1b068f62-8b12-434f-b7aa-66c5fd48daef.jpeg" },
          { id: 3183, url: "https://maximgun.ru/uploads/1755017601383_5ac09a5e-355e-4662-a40c-e77da812aab9.jpeg" },
          { id: 3182, url: "https://maximgun.ru/uploads/1755017600643_a424368c-b581-4f50-aecb-fc2f2c63da87.jpeg" },
          { id: 3181, url: "https://maximgun.ru/uploads/1755017600235_bbe4651d-ddad-47d4-a614-0182535f72ef.jpeg" },
        ], // https://maximgun.ru/details/126
      },
      4681: { featured: null, photos: [] },
      5551: {
        featured: 6309,
        photos: [
          { id: 6309, url: "https://maximgun.ru/uploads/1769181291040_3e54109c-efad-4e88-aa41-64c84a6ab4f8.jpg" },
        ], // https://maximgun.ru/details/355
      },
    },
    rearRuler: {
      2758: {
        featured: null,
        photos: [
          { id: 3187, url: "https://maximgun.ru/uploads/1755017603521_0fb5a0a4-0a33-4dc8-b2f6-9279f663814e.jpeg" },
          { id: 3189, url: "https://maximgun.ru/uploads/1755017604655_3e2592cf-6088-48a0-97aa-25b9756815ea.jpeg" },
          { id: 3188, url: "https://maximgun.ru/uploads/1755017604066_9dc1edc0-4572-4db4-97ac-61b5ce5956d0.jpeg" },
          { id: 3186, url: "https://maximgun.ru/uploads/1755017603039_f512cc96-5e71-44aa-93dd-cddcd20998a0.jpeg" },
          { id: 3185, url: "https://maximgun.ru/uploads/1755017602400_f1c954b5-5fc9-4b56-a13f-8e56d47c775b.jpeg" },
          { id: 3180, url: "https://maximgun.ru/uploads/1755017599713_079289d1-2987-45c0-941c-d64b4c401f8f.jpeg" },
          { id: 3184, url: "https://maximgun.ru/uploads/1755017601867_1b068f62-8b12-434f-b7aa-66c5fd48daef.jpeg" },
          { id: 3183, url: "https://maximgun.ru/uploads/1755017601383_5ac09a5e-355e-4662-a40c-e77da812aab9.jpeg" },
          { id: 3182, url: "https://maximgun.ru/uploads/1755017600643_a424368c-b581-4f50-aecb-fc2f2c63da87.jpeg" },
          { id: 3181, url: "https://maximgun.ru/uploads/1755017600235_bbe4651d-ddad-47d4-a614-0182535f72ef.jpeg" },
        ], // https://maximgun.ru/details/126
      },
      4681: { featured: null, photos: [] },
      5551: { featured: null, photos: [] },
    },
    coarse: {
      2458: {
        featured: 277,
        photos: [
          { id: 277, url: "https://maximgun.ru/uploads/1753870578653_153c52eb-1fc8-443e-8547-3e57f8d0f1a4.jpg" },
        ], // https://maximgun.ru/details/81
      },
      3979: { featured: null, photos: [] },
      4615: { featured: null, photos: [] },
      5151: {
        featured: null,
        photos: [
          { id: 304, url: "https://maximgun.ru/uploads/1753904427791_991552dc-141c-44aa-93aa-ae19d3d86c84.jpg" },
          { id: 303, url: "https://maximgun.ru/uploads/1753904427418_91e0f5c3-2241-4201-b6c9-0b94c14c61a4.jpg" },
          { id: 30, url: "https://maximgun.ru/uploads/1752765359351_420ccee4-1264-40b4-9e40-982c0a03ac42.png" },
          { id: 305, url: "https://maximgun.ru/uploads/1753904482916_a2cd2b99-1eec-4a9d-a1e3-64afdb8bc0f6.jpg" },
          { id: 306, url: "https://maximgun.ru/uploads/1753904559080_cd55d2f0-b51c-4579-9b8c-7b13ee5fd02a.jpg" },
          { id: 302, url: "https://maximgun.ru/uploads/1753904427088_08550a8d-c2df-48ea-b3ac-24ac7a86a8f6.jpg" },
          { id: 301, url: "https://maximgun.ru/uploads/1753904426718_8b385869-b42f-4b8b-8419-5eec7258b51f.jpg" },
          { id: 293, url: "https://maximgun.ru/uploads/1753903232993_aa41829a-28e0-476a-a09f-fabfddaa33d1.png" },
          { id: 292, url: "https://maximgun.ru/uploads/1753903232573_1593d40f-8d2a-4001-aa65-4b17fe7cf853.jpeg" },
          { id: 291, url: "https://maximgun.ru/uploads/1753903232191_d4147ee1-6ed6-4d9b-9ce0-3b9ba93c08e9.jpeg" },
          { id: 31, url: "https://maximgun.ru/uploads/1752765359819_f6abc718-ce68-479a-a47c-0f8391ceeb58.png" },
          { id: 6447, url: "https://maximgun.ru/uploads/1769718014230_21689fd0-2ee9-45dc-9010-32012287dc63.jpg" },
          { id: 6446, url: "https://maximgun.ru/uploads/1769718009880_b27daed1-e453-4791-a1d2-cc4a7fa1d42e.jpg" },
          { id: 6445, url: "https://maximgun.ru/uploads/1769718005677_5b3ea6e8-d70a-4c7a-8a9b-cf4066662f36.jpg" },
        ], // https://maximgun.ru/details/20
      },
    },
    link: {
      2225: {
        featured: 5322,
        photos: [
          { id: 5322, url: "https://maximgun.ru/uploads/1761156726963_738caf80-24bb-43f3-94ab-b2511b8571f0.jpg" },
        ], // https://maximgun.ru/details/232
      },
      5429: {
        featured: null,
        photos: [
          { id: 3192, url: "https://maximgun.ru/uploads/1755017720859_3639df4d-8bba-4dde-98df-6786c7698d85.jpeg" },
          { id: 3191, url: "https://maximgun.ru/uploads/1755017720254_1c37e88f-ae73-43da-9884-b03f913096dc.jpeg" },
          { id: 3190, url: "https://maximgun.ru/uploads/1755017719636_815dedfb-3d12-4748-997b-77bf9d9caa57.jpeg" },
        ], // https://maximgun.ru/details/127
      },
    },
  },
};

function featurePhotoSet(manufacturer, featureKey, point) {
  if (!point) return { featured: null, photos: [] };
  return FEATURE_PHOTOS?.[manufacturer]?.[featureKey]?.[point.n] || { featured: null, photos: [] };
}

function featuredPhoto(photoSet) {
  if (!photoSet?.featured || !photoSet?.photos?.length) return null;
  return photoSet.photos.find((photo) =>
    photo.id === photoSet.featured || photo.url === photoSet.featured
  ) || null;
}


function periodBase(man,year){
 if(!year || !BASE[man]) return null;
 for(const block of Object.values(BASE[man])){
   if(block.years && block.years.includes(year)) return block;
 }
 return null;
}

function refPriority(point){
 if(point.url?.includes("/details/")) return 0;
 if(point.url?.includes("/products/")) return 1;
 return 2;
}

function prioritizeRefs(points){
 return [...points].sort((a,b)=>{
   const p=refPriority(a)-refPriority(b);
   if(p!==0) return p;
   return (a.n||0)-(b.n||0);
 });
}

function stateSignature(point){
 return (point?.f||[]).join("|");
}

function featureContext(man,key,serial,year){
 const all=((POINTS[man]||{})[key]||[]).slice().sort((a,b)=>a.n-b.n);
 const boundaries=all.filter((point)=>point.boundary!==false);
 const previous=serial ? [...boundaries].reverse().find((point)=>point.n<serial) || null : null;
 const next=serial ? boundaries.find((point)=>point.n>serial) || null : null;
 const neighbors=serial
   ? [previous,next].filter(Boolean).sort((a,b)=>Math.abs(a.n-serial)-Math.abs(b.n-serial))
   : [];
 const sameYear=year ? prioritizeRefs(all.filter((point)=>point.y===year)).slice(0,8) : [];
 const first=boundaries[0] || null;
 const last=boundaries[boundaries.length-1] || null;
 const transition=previous && next && stateSignature(previous)!==stateSignature(next)
   ? "Известная граница находится между №" + previous.n + " и №" + next.n + "."
   : null;

 return {
  previous,
  next,
  neighbors,
  sameYear,
  first,
  last,
  transition,
  allDetails: all,
  serial,
  year,
 };
}

function infer(man,key,serial,year){
 const all=((POINTS[man]||{})[key]||[]).slice().sort((a,b)=>a.n-b.n);
 const boundaries=all.filter((point)=>point.boundary!==false);
 const base=periodBase(man,year);
 const baseFacts=base && base[key] ? base[key] : [];
 const context=featureContext(man,key,serial,year);
 const done=(value)=>({...value,context});

 if(serial){
   const exact=all.find((point)=>point.n===serial);
   if(exact){
     const direct=exact.boundary!==false && exact.c==="high";
     return done({
       facts:exact.f,
       c:exact.c,
       reason:direct
         ? "Есть прямая номерная деталь именно для этого узла."
         : exact.boundary===false
           ? "Для этого номера есть полезное наблюдение или реконструкция, но использовать её как жёсткую серийную границу нельзя."
           : "Есть номерная деталь, но степень уверенности ограничена сохранностью или качеством материала.",
       refs:[exact]
     });
   }

   const prev=[...boundaries].reverse().find((point)=>point.n<serial);
   const next=boundaries.find((point)=>point.n>serial);

   if(prev && next){
     const ordered=[prev,next].sort((a,b)=>Math.abs(a.n-serial)-Math.abs(b.n-serial));
     const nearest=ordered[0];
     const farther=ordered[1];
     const nearestDistance=Math.abs(nearest.n-serial);
     const fartherDistance=Math.abs(farther.n-serial);

     if(stateSignature(prev)===stateSignature(next)){
       return done({
         facts:nearest.f,
         c:"medium",
         reason:`Одинаковое состояние известно по обе стороны номера. Ближайшая деталь — №${nearest.n} (разница ${nearestDistance}), поэтому она показана первой.`,
         refs:ordered
       });
     }

     return done({
       facts:nearest.f,
       c:"medium",
       reason:`Точная граница изменения находится между №${prev.n} и №${next.n}. По серийному номеру ближе №${nearest.n} (разница ${nearestDistance}; до №${farther.n} — ${fartherDistance}), поэтому наиболее вероятно исполнение, близкое к №${nearest.n}.`,
       refs:ordered
     });
   }

   // Important: a terminal late state can be inherited forward instead of becoming "unknown".
   if(prev?.carry && (!year || !prev.y || prev.y<=year)){
     return done({
       facts:prev.f,
       c:"medium",
       reason:`Последнее подтверждённое состояние — №${prev.n}. Для этого узла оно считается устойчивым дальше по серии: возврата к предыдущей конструкции не зафиксировано.`,
       refs:[prev]
     });
   }

   if(baseFacts.length){
     const refs=[prev,next].filter(Boolean).sort((a,b)=>Math.abs(a.n-serial)-Math.abs(b.n-serial));
     return done({
       facts:baseFacts,
       c:base._confidence||"medium",
       reason:base._reason||"Использована ожидаемая конфигурация выбранного периода; ближайшие номерные детали приведены для контроля.",
       refs
     });
   }

   if(prev) return done({
     facts:prev.f,
     c:"low",
     reason:`Ближайшая более ранняя номерная деталь — №${prev.n}; дальнейшая граница пока неизвестна.`,
     refs:[prev]
   });

   if(next) return done({
     facts:next.f,
     c:"low",
     reason:`Ближайшая более поздняя номерная деталь — №${next.n}; для меньших номеров граница пока неизвестна.`,
     refs:[next]
   });
 }

 if(year && baseFacts.length){
   const same=all.filter((point)=>point.y===year);
   const directStates=new Set(same.filter((point)=>point.boundary!==false).map(stateSignature));
   const transitionNote=directStates.size>1
     ? ` Внутри ${year} года известны разные исполнения этого узла; они показаны ниже среди номерных деталей.`
     : "";

   return done({
     facts:baseFacts,
     c:base._confidence||"medium",
     reason:(base._reason||"Показана ожидаемая конфигурация периода; для конкретного станка нужна сверка по номерным деталям.")+transitionNote,
     refs:prioritizeRefs(same).slice(0,4)
   });
 }

 if(year){
   const same=all.filter((point)=>point.y===year);
   if(same.length){
     const direct=same.filter((point)=>point.boundary!==false);
     const signatures=new Set(direct.map(stateSignature));
     if(direct.length && signatures.size===1){
       return done({
         facts:direct[0].f,
         c:"medium",
         reason:`Для ${year} года известны согласующиеся номерные детали, но общей периодной схемы по этому узлу пока нет.`,
         refs:prioritizeRefs(same).slice(0,4)
       });
     }
     return done({
       facts:["В этом году зафиксировано несколько состояний узла; без серийного номера выбрать одно из них нельзя."],
       c:"low",
       reason:"Смотрите известные номерные детали ниже — они показывают сам переход.",
       refs:prioritizeRefs(same).slice(0,4)
     });
   }
 }

 if(baseFacts.length) return done({
   facts:baseFacts,
   c:base._confidence||"medium",
   reason:base._reason||"Показана ожидаемая конфигурация периода.",
   refs:[]
 });

 return done({
   facts:["Данных пока недостаточно."],
   c:"low",
   reason:"Для этого сочетания пока нет надёжной общей схемы или номерных деталей.",
   refs:[]
 });
}

function tone(isDark) {
  return {
    panel: isDark
      ? 'border-white/10 bg-white/[0.035]'
      : 'border-gray-200 bg-white/75',
    panelStrong: isDark
      ? 'border-white/10 bg-white/[0.055]'
      : 'border-gray-200 bg-white',
    text: isDark ? 'text-white' : 'text-gray-950',
    muted: isDark ? 'text-gray-400' : 'text-gray-600',
    subtle: isDark ? 'text-gray-500' : 'text-gray-500',
    input: isDark
      ? 'border-white/10 bg-white/[0.055] text-white placeholder:text-gray-500'
      : 'border-gray-300 bg-gray-50 text-gray-950 placeholder:text-gray-400',
    chip: isDark
      ? 'border-white/10 bg-white/5 text-gray-300'
      : 'border-gray-200 bg-gray-50 text-gray-600',
  };
}

function sourceType(point, labels){
 if(point.url?.includes("/details/")) return labels.sourceDetail;
 if(point.url?.includes("/products/")) return labels.sourceExample;
 return labels.sourceSearch;
}

function EvidencePoint({
  point,
  caption,
  isDark,
  manufacturer,
  featureKey,
  onOpenPhotos,
  labels,
  isCurrent = false,
}) {
  const c = tone(isDark);
  if (!point) return null;

  const photoSet = featurePhotoSet(manufacturer, featureKey, point);
  const featured = featuredPhoto(photoSet);
  const featuredIndex = featured
    ? Math.max(0, photoSet.photos.findIndex((photo) => photo === featured))
    : 0;

  const openPhotos = (index = 0) => {
    if (!photoSet.photos?.length) return;
    onOpenPhotos?.(photoSet.photos, index);
  };

  return (
    <div
      className={[
        'rounded-xl border p-3',
        isCurrent
          ? isDark
            ? 'border-amber-400/40 bg-amber-500/[0.06] ring-1 ring-amber-400/20'
            : 'border-amber-300 bg-amber-50 ring-1 ring-amber-200'
          : isDark
            ? 'border-white/10 bg-black/10'
            : 'border-gray-200 bg-gray-50',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`text-[10px] font-extrabold uppercase tracking-wider ${c.subtle}`}>{caption}</span>
        <span className={`text-[10px] ${c.subtle}`}>{sourceType(point, labels)}</span>
      </div>

      <a
        href={point.url}
        target="_blank"
        rel="noreferrer"
        className={`mt-1 inline-flex text-xs font-bold no-underline ${isDark ? 'text-amber-300 hover:text-amber-200' : 'text-amber-800 hover:text-amber-700'}`}
      >
        {point.label} →
      </a>

      <p className={`mb-0 mt-1 text-[11px] leading-5 ${c.muted}`}>
        {(point.f || []).join(' ')}
      </p>

      {featured && (
        <button
          type="button"
          onClick={() => openPhotos(featuredIndex)}
          className="mt-3 block w-full overflow-hidden rounded-lg border-0 bg-transparent p-0 text-left"
          title={`${labels.openPhoto}: ${point.label}`}
        >
          <img
            src={featured.url}
            alt={point.label}
            loading="lazy"
            className="h-32 w-full object-cover transition duration-200 hover:scale-[1.02]"
          />
        </button>
      )}

      {photoSet.photos?.length > 0 && (
        <button
          type="button"
          onClick={() => openPhotos(featuredIndex)}
          className={[
            'mt-2 inline-flex items-center rounded-lg border px-2.5 py-1.5 text-[11px] font-bold transition',
            isDark
              ? 'border-white/10 bg-white/5 text-gray-200 hover:bg-white/10'
              : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-100',
          ].join(' ')}
        >
          {featured
            ? `${labels.openGallery} (${photoSet.photos.length})`
            : `${labels.partPhotos} (${photoSet.photos.length})`}
        </button>
      )}
    </div>
  );
}

function FeatureCard({ title, result, isDark, manufacturer, featureKey, onOpenPhotos, labels }) {
  const c = tone(isDark);
  const context = result.context || {};
  const knownDetails = (context.allDetails || []).slice().sort((a, b) => a.n - b.n);
  const serial = context.serial || null;
  const year = context.year || null;

  const exactSerialIndex = serial
    ? knownDetails.findIndex((point) => point.n === serial)
    : -1;

  const exactYearIndexes = year
    ? knownDetails
        .map((point, index) => (point.y === year ? index : -1))
        .filter((index) => index >= 0)
    : [];

  const markerBeforeIndex = (() => {
    if (!knownDetails.length) return -1;

    if (serial) {
      // При точном совпадении отдельную линию не вставляем:
      // сама карточка детали будет выделена как «МЫ ТУТ».
      if (exactSerialIndex >= 0) return -1;

      const nextIndex = knownDetails.findIndex((point) => point.n > serial);
      return nextIndex >= 0 ? nextIndex : knownDetails.length;
    }

    if (year) {
      // Если есть детали именно этого года — ставим маркер перед первой из них.
      if (exactYearIndexes.length) return exactYearIndexes[0];

      // Если прямой детали этого года нет — ставим год между ближайшими
      // известными годами. Детали без установленного года не используем
      // для расчёта положения маркера.
      const nextIndex = knownDetails.findIndex(
        (point) => Number.isFinite(point.y) && point.y > year
      );

      return nextIndex >= 0 ? nextIndex : knownDetails.length;
    }

    return -1;
  })();

  const markerLabel = serial
    ? `${labels.here} · №${serial}`
    : year
      ? `${labels.here} · ${year}${labels.yearSuffix ? ` ${labels.yearSuffix.toUpperCase()}` : ''}`
      : null;

  const hereMarker = markerLabel ? (
    <div
      className={[
        'flex items-center gap-3 py-1',
        isDark ? 'text-amber-300' : 'text-amber-800',
      ].join(' ')}
    >
      <div className={`h-px flex-1 ${isDark ? 'bg-amber-400/30' : 'bg-amber-300'}`} />
      <div
        className={[
          'shrink-0 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-wider',
          isDark
            ? 'border-amber-400/30 bg-amber-400/10 text-amber-300'
            : 'border-amber-300 bg-amber-50 text-amber-800',
        ].join(' ')}
      >
        {markerLabel}
      </div>
      <div className={`h-px flex-1 ${isDark ? 'bg-amber-400/30' : 'bg-amber-300'}`} />
    </div>
  ) : null;

  return (
    <div className={`relative rounded-2xl border p-4 sm:p-5 ${c.panel}`}>
      <div className="mb-3">
        <h3 className={`m-0 text-base font-bold ${c.text}`}>{title}</h3>
      </div>

      <ul className={`m-0 space-y-1.5 pl-5 text-sm leading-6 ${c.text}`}>
        {result.facts.map((fact, index) => (
          <li key={`${title}-${index}`}>{fact}</li>
        ))}
      </ul>

      {knownDetails.length > 0 && (
        <details className={`mt-4 rounded-xl border ${isDark ? 'border-white/10 bg-white/[0.02]' : 'border-gray-200 bg-white/60'}`}>
          <summary className={`cursor-pointer select-none px-3 py-2.5 text-xs font-bold ${c.text}`}>
            {labels.knownParts}
          </summary>

          <div className={`border-t p-3 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <div className="space-y-2">
              {knownDetails.map((point, index) => {
                const isCurrentSerial = !!serial && point.n === serial;
                const isCurrentYear = !!year && point.y === year;
                const isCurrent = isCurrentSerial || isCurrentYear;

                return (
                  <div key={`${point.label}-${point.n}-${point.url}-${index}`} className="space-y-2">
                    {markerBeforeIndex === index && hereMarker}

                    <EvidencePoint
                      point={point}
                      caption={
                        isCurrentSerial
                          ? `${labels.here} · ${labels.knownPart}`
                          : isCurrentYear
                            ? labels.partFromYear(year)
                            : point.boundary === false
                              ? labels.observation
                              : labels.knownPart
                      }
                      isDark={isDark}
                      manufacturer={manufacturer}
                      featureKey={featureKey}
                      onOpenPhotos={onOpenPhotos}
                      labels={labels}
                      isCurrent={isCurrent}
                    />
                  </div>
                );
              })}

              {markerBeforeIndex === knownDetails.length && hereMarker}
            </div>
          </div>
        </details>
      )}
    </div>
  );
}

function ExampleCard({ item, isDark, onOpenPhotos }) {
  const c = tone(isDark);
  const photos = item.img
    ? [{ id: item.photoId ?? null, url: item.img }]
    : [];

  return (
    <article className={`overflow-hidden rounded-2xl border ${c.panel}`}>
      {photos.length > 0 ? (
        <button
          type="button"
          onClick={() => onOpenPhotos?.(photos, 0)}
          className="block w-full border-0 bg-transparent p-0 text-left"
          title={`Открыть фото: ${item.title}`}
        >
          <img
            src={photos[0].url}
            alt={item.title}
            loading="lazy"
            className="h-48 w-full object-cover"
          />
        </button>
      ) : (
        <a href={item.url} target="_blank" rel="noreferrer" className="block no-underline">
          <div className={`flex h-32 w-full items-center justify-center text-xs font-semibold ${isDark ? 'bg-black/20 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
            Открыть экземпляр в базе →
          </div>
        </a>
      )}

      <div className="p-4">
        <h3 className={`m-0 text-base font-bold ${c.text}`}>{item.title}</h3>
        <p className={`mb-0 mt-2 text-sm leading-6 ${c.muted}`}>{item.desc}</p>
        <a
          href={item.url}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex text-sm font-bold text-amber-500 no-underline hover:text-amber-400"
        >
          Подробнее →
        </a>
      </div>
    </article>
  );
}

// function ComparisonBlock({ isDark }) {
//   const c = tone(isDark);
//   const boxClass = `rounded-2xl border p-4 sm:p-5 ${c.panel}`;
//
//   return (
//     <section className="mt-7">
//       <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
//         <h2 className={`m-0 text-xl font-extrabold sm:text-2xl ${c.text}`}>
//           Быстрое отличие поздних ТОЗ и ОРЗ
//         </h2>
//         <span className={`text-xs ${c.subtle}`}>рабочие визуальные признаки</span>
//       </div>
//
//       <div className="grid gap-3 md:grid-cols-2">
//         <div className={boxClass}>
//           <h3 className={`m-0 mb-3 text-lg font-bold ${c.text}`}>ТОЗ</h3>
//           <ul className={`m-0 space-y-2 pl-5 text-sm leading-6 ${c.muted}`}>
//             <li>№5691 уже имеет грубые утолщённые стальные лыжи на 16 отверстий; позднее появляются бронзовые на 6 отверстий.</li>
//             <li>К №10679 пропил под ноги исчезает, а ноги уже отсутствуют.</li>
//             <li>Вертлюг: №4541 стальной с боковыми отверстиями → №6134/6612 бронзовый с отверстиями → с №8037 бронзовый без них.</li>
//             <li>На поздней округлой боевой оси выборка под стопор стола заметно крупнее, чем у ОРЗ.</li>
//             <li>Механизм блокировки хобота: №6799 ещё сложный → к №8037 уже упрощённый.</li>
//           </ul>
//         </div>
//
//         <div className={boxClass}>
//           <h3 className={`m-0 mb-3 text-lg font-bold ${c.text}`}>ОРЗ</h3>
//           <ul className={`m-0 space-y-2 pl-5 text-sm leading-6 ${c.muted}`}>
//             <li>Все известные поздние лыжи остаются стальными, тонкими и аккуратно обработанными.</li>
//             <li>Поздняя схема лыж: 8 отверстий, без пропила под ноги, с технологическим отверстием спереди.</li>
//             <li>К №4681 лапки хобота уже бронзовые без фигурной проточки, механизм блокировки упрощён, хомут без «рогов».</li>
//             <li>У стола посадка задней линейки исчезает раньше боковых вырезов под нажимы; стол №5551, 1917 подтверждает это напрямую.</li>
//             <li>На поздней округлой боевой оси выборка под стопор стола небольшая.</li>
//           </ul>
//         </div>
//       </div>
//     </section>
//   );
// }

export default function SokolovIdentifier({ isDark: isDarkOverride }) {
  const { isDark: themeIsDark } = useTheme();
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;
  const isDark = isDarkOverride ?? themeIsDark;
  const c = tone(isDark);

  const [manufacturer, setManufacturer] = useState('TOZ');
  const [searchMode, setSearchMode] = useState('year');
  const [year, setYear] = useState('');
  const [serial, setSerial] = useState('');
  const [query, setQuery] = useState(null);
  const [photoViewer, setPhotoViewer] = useState({
    open: false,
    photos: [],
    index: 0,
  });

  const openPhotos = (photos, index = 0) => {
    if (!photos?.length) return;
    setPhotoViewer({
      open: true,
      photos,
      index: Math.min(Math.max(index, 0), photos.length - 1),
    });
  };

  const parsedSerial = (value) => {
    if (!value) return null;
    const digits = String(value).replace(/\D/g, '');
    return digits ? Number(digits) : null;
  };

  const run = () => {
    const parsed = parsedSerial(serial);

    if (searchMode === 'serial') {
      if (!parsed) return;

      setQuery({
        manufacturer,
        year: null,
        serial: parsed,
      });
      return;
    }

    if (!year) return;

    setQuery({
      manufacturer,
      year: Number(year),
      serial: null,
    });
  };

  const changeSearchMode = (mode) => {
    setSearchMode(mode);

    if (mode === 'serial') {
      setYear('');
    } else {
      setSerial('');
    }
  };

  const useExample = () => {
    setManufacturer('TOZ');
    setSearchMode('serial');
    setYear('');
    setSerial('1280');
    setQuery({ manufacturer: 'TOZ', year: null, serial: 1280 });
  };

  const results = useMemo(() => {
    if (!query) return null;

    if (query.manufacturer === 'UNKNOWN') {
      return ['TOZ', 'ORZ'].map((man) => ({
        manufacturer: man,
        features: FEATURES.map((feature) => ({
          ...feature,
          result: infer(man, feature.key, query.serial, query.year),
        })),
      }));
    }

    return [{
      manufacturer: query.manufacturer,
      features: FEATURES.map((feature) => ({
        ...feature,
        result: infer(query.manufacturer, feature.key, query.serial, query.year),
      })),
    }];
  }, [query]);

  const examples = useMemo(() => {
    if (!query) return [];

    let pool = EXAMPLES.slice();

    if (query.manufacturer !== 'UNKNOWN') {
      pool = pool.filter((item) => item.m === query.manufacturer);
    }

    if (query.year) {
      const exactYear = pool.filter((item) => item.y === query.year);
      pool = exactYear.length >= 2
        ? exactYear
        : pool.filter((item) => Math.abs(item.y - query.year) <= 1);
    }

    pool.sort((a, b) => {
      const yearA = query.year ? Math.abs(a.y - query.year) : 0;
      const yearB = query.year ? Math.abs(b.y - query.year) : 0;
      if (yearA !== yearB) return yearA - yearB;

      const numberA = query.serial && Number.isFinite(a.n) ? Math.abs(a.n - query.serial) : 0;
      const numberB = query.serial && Number.isFinite(b.n) ? Math.abs(b.n - query.serial) : 0;
      return numberA - numberB;
    });

    return pool.slice(0, 5);
  }, [query]);

  const manufacturerLabel = (value) => {
    if (value === 'TOZ') return lang === 'EN' ? 'TOZ' : 'ТОЗ';
    if (value === 'ORZ') return lang === 'EN' ? 'ORZ' : 'ОРЗ';
    return labels.unknownManufacturer;
  };

  return (
    <div className="not-prose w-full">
      <div className="w-full px-3 py-4 sm:px-4 sm:py-5 lg:px-10 xl:px-16 2xl:px-20">
      <section
        className={[
          'grid overflow-hidden rounded-3xl border md:grid-cols-[minmax(0,1fr)_390px]',
          isDark
            ? 'border-white/10 bg-gradient-to-br from-white/[0.055] to-white/[0.025]'
            : 'border-gray-200 bg-gradient-to-br from-white to-gray-50',
        ].join(' ')}
      >
        <div className="flex flex-col justify-center p-6 sm:p-8 md:p-10">
          <h2 className={`m-0 text-4xl font-black leading-[0.98] tracking-tight sm:text-5xl ${c.text}`}>
            {labels.title}
          </h2>

          <p className={`mb-0 mt-5 max-w-2xl text-base leading-7 ${c.muted}`}>
            {labels.intro}
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
{/*             <span className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${c.chip}`}> */}
{/*               1909–1917 */}
{/*             </span> */}
{/*             <span className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${c.chip}`}> */}
{/*               ТОЗ / ОРЗ */}
{/*             </span> */}
          </div>
        </div>

        <div className={`m-4 rounded-2xl border p-4 sm:m-5 sm:p-5 ${c.panelStrong}`}>
          <h3 className={`m-0 text-xl font-extrabold ${c.text}`}>{labels.question}</h3>

          <p className={`mb-4 mt-1 text-xs ${c.muted}`}>{labels.searchHelp}</p>

          <label className={`mb-1.5 block text-xs font-bold ${c.text}`}>{labels.manufacturer}</label>
          <select
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            className={`mb-3 w-full rounded-xl border px-3 py-2.5 text-sm outline-none ${c.input}`}
          >
            <option value="TOZ">{labels.toz}</option>
            <option value="ORZ">{labels.orz}</option>
            <option value="UNKNOWN">{labels.unknownMaker}</option>
          </select>

          <label className={`mb-1.5 mt-4 block text-xs font-bold ${c.text}`}>{labels.search}</label>
          <div className={`mb-3 grid grid-cols-2 rounded-xl border p-1 ${isDark ? 'border-white/10 bg-white/[0.025]' : 'border-gray-200 bg-gray-50'}`}>
            <button
              type="button"
              onClick={() => changeSearchMode('serial')}
              className={[
                'rounded-lg px-3 py-2 text-xs font-bold transition',
                searchMode === 'serial'
                  ? isDark
                    ? 'bg-white/10 text-white'
                    : 'bg-white text-gray-950 shadow-sm'
                  : c.muted,
              ].join(' ')}
            >
              {labels.bySerial}
            </button>
            <button
              type="button"
              onClick={() => changeSearchMode('year')}
              className={[
                'rounded-lg px-3 py-2 text-xs font-bold transition',
                searchMode === 'year'
                  ? isDark
                    ? 'bg-white/10 text-white'
                    : 'bg-white text-gray-950 shadow-sm'
                  : c.muted,
              ].join(' ')}
            >
              {labels.byYear}
            </button>
          </div>

          {searchMode === 'year' ? (
            <>
              <label className={`mb-1.5 block text-xs font-bold ${c.text}`}>{labels.year}</label>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none ${c.input}`}
              >
                <option value="">{labels.selectYear}</option>
                {[1909,1910,1911,1912,1913,1914,1915,1916,1917].map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label className={`mb-1.5 block text-xs font-bold ${c.text}`}>{labels.serial}</label>
              <input
                value={serial}
                onChange={(e) => setSerial(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && run()}
                inputMode="numeric"
                placeholder={labels.serialPlaceholder}
                className={`w-full rounded-xl border px-3 py-2.5 text-sm outline-none ${c.input}`}
              />
            </>
          )}

          <div className="mt-4 grid grid-cols-[1fr_auto] gap-2">
            <button
              type="button"
              onClick={run}
              disabled={searchMode === 'serial' ? !parsedSerial(serial) : !year}
              className={[
                'sokolov-submit rounded-xl px-4 py-2.5 text-sm font-extrabold text-white transition',
                (searchMode === 'serial' ? !parsedSerial(serial) : !year)
                  ? 'cursor-not-allowed bg-amber-900/50 opacity-50'
                  : 'bg-amber-600 hover:bg-amber-500',
              ].join(' ')}
            >
              {labels.show}
            </button>
{/*             <button */}
{/*               type="button" */}
{/*               onClick={useExample} */}
{/*               className={[ */}
{/*                 'rounded-xl border px-4 py-2.5 text-sm font-bold transition', */}
{/*                 isDark */}
{/*                   ? 'border-white/10 bg-white/5 text-white hover:bg-white/10' */}
{/*                   : 'border-gray-200 bg-gray-50 text-gray-900 hover:bg-gray-100', */}
{/*               ].join(' ')} */}
{/*             > */}
{/*               Пример */}
{/*             </button> */}
          </div>
        </div>
      </section>

      {query && (
        <>
          <section className={`mt-5 rounded-2xl border p-5 ${c.panel}`}>
            <h2 className={`m-0 text-2xl font-extrabold ${c.text}`}>
              {manufacturerLabel(query.manufacturer)}
              {query.serial ? ` · №${query.serial}` : ''}
              {query.year ? ` · ${query.year}${labels.yearSuffix ? ` ${labels.yearSuffix}` : ''}` : ''}
            </h2>
            <p className={`mb-0 mt-2 text-sm ${c.muted}`}>
              {query.serial ? labels.serialResult : labels.yearResult}
            </p>
          </section>

          {query.manufacturer === 'UNKNOWN' ? (
            <section className="mt-4 space-y-5">
              {FEATURES.map((featureMeta) => {
                const tozGroup = results?.find((group) => group.manufacturer === 'TOZ');
                const orzGroup = results?.find((group) => group.manufacturer === 'ORZ');

                const tozFeature = tozGroup?.features.find((feature) => feature.key === featureMeta.key);
                const orzFeature = orzGroup?.features.find((feature) => feature.key === featureMeta.key);

                return (
                  <div key={`compare-${featureMeta.key}`}>
                    <h2 className={`mb-3 mt-0 text-xl font-extrabold ${c.text}`}>
                      {featureTitle(featureMeta, lang)}
                    </h2>

                    <div className="grid gap-3 lg:grid-cols-2">
                      {tozFeature && (
                        <div>
                          <div className={`mb-2 text-xs font-black uppercase tracking-wider ${c.subtle}`}>
                            {lang === 'EN' ? 'TOZ' : 'ТОЗ'}
                          </div>
                          <FeatureCard
                            title={lang === 'EN' ? 'TOZ' : 'ТОЗ'}
                            result={tozFeature.result}
                            isDark={isDark}
                            manufacturer="TOZ"
                            featureKey={featureMeta.key}
                            onOpenPhotos={openPhotos}
                            labels={labels}
                          />
                        </div>
                      )}

                      {orzFeature && (
                        <div>
                          <div className={`mb-2 text-xs font-black uppercase tracking-wider ${c.subtle}`}>
                            {lang === 'EN' ? 'ORZ' : 'ОРЗ'}
                          </div>
                          <FeatureCard
                            title={lang === 'EN' ? 'ORZ' : 'ОРЗ'}
                            result={orzFeature.result}
                            isDark={isDark}
                            manufacturer="ORZ"
                            featureKey={featureMeta.key}
                            onOpenPhotos={openPhotos}
                            labels={labels}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          ) : (
            results?.map((group) => (
              <section key={group.manufacturer} className="mt-4">
                <div className="grid gap-3 lg:grid-cols-2">
                  {group.features.map((feature) => (
                    <FeatureCard
                      key={`${group.manufacturer}-${feature.key}`}
                      title={featureTitle(feature, lang)}
                      result={feature.result}
                      isDark={isDark}
                      manufacturer={group.manufacturer}
                      featureKey={feature.key}
                      onOpenPhotos={openPhotos}
                      labels={labels}
                    />
                  ))}
                </div>
              </section>
            ))
          )}

{/*           {examples.length > 0 && ( */}
{/*             <section className="mt-7"> */}
{/*               <div className="mb-3 flex flex-wrap items-end justify-between gap-2"> */}
{/*                 <h2 className={`m-0 text-xl font-extrabold sm:text-2xl ${c.text}`}> */}
{/*                   Известные экземпляры для сравнения */}
{/*                 </h2> */}
{/*                 <span className={`text-xs ${c.subtle}`}>подобраны под введённые данные</span> */}
{/*               </div> */}

{/*               <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"> */}
{/*                 {examples.map((item) => ( */}
{/*                   <ExampleCard key={`${item.title}-${item.n}`} item={item} isDark={isDark} onOpenPhotos={openPhotos} /> */}
{/*                 ))} */}
{/*               </div> */}
{/*             </section> */}
{/*           )} */}
        </>
      )}

{/*       <ComparisonBlock isDark={isDark} /> */}

      <PhotoLightboxWithInfo
        photos={photoViewer.photos}
        open={photoViewer.open}
        index={photoViewer.index}
        onClose={() => setPhotoViewer((prev) => ({ ...prev, open: false }))}
        onIndexChange={(index) => setPhotoViewer((prev) => ({ ...prev, index }))}
      />

      </div>
    </div>
  );
}
